/**
 * MandateDetailV3 — agent-centric panel layout.
 *
 * Phase A (this file): single-record view reorganized into three tabs.
 * The record's subject is presented as an agent identity, not a raw
 * audit event. Order details, search results, and refund details surface
 * inside the Activity trace alongside the policy and decision data.
 *
 * Phase B (follow-up): replace the Activity tab stub with a per-agent
 * history query so the tab shows all records for this agent_id on this site.
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { AlertOctagon, Check, ChevronDown, ChevronRight, Copy, Download, FileJson, Flag, Info, ShieldAlert, ShieldCheck, TriangleAlert, X } from 'lucide-react';

import { humanRecordDevice, humanRecordName, identityState, isHumanVisit } from '@sill/audit';

import type { AuditRecord, AuditRecordPayload } from '@sill/types';
import type { BlockingState, VisitGroup } from '@sill/types/api';
import { inferUaProductToken, knownPublicSignerFor, publicSignerDisplayFor } from '@sill/types';
import { isSillCredentialSigner, sillSlugFromSigner } from '@sill/webbotauth';

import { useApi } from '../../api/api-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { getBlocking } from '../../api/threats.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useC } from '../../lib/theme.js';
import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { formatIsoDate, formatMinor } from '../../lib/format.js';
import { Card, Pill, type PillTone, RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { BuyerDetailPanel } from '../audit/BuyerDetailPanel.js';
import { CountryFlag, countryName } from '../../components/atoms/CountryFlag.js';
import { OriginalOrderPanel } from '../audit/OriginalOrderPanel.js';
import { shortPolicyLabel } from './effective-outcome.js';
import { type BundleSplit, exportAuditBundleHTML, exportErrorMessage, exportAuditRecordJSON } from './export.js';
import { NarrowedExportPanel } from './NarrowedExportPanel.js';
import { BundleDayNote } from './BundleDayNote.js';
import { BundlePartsNote } from './BundlePartsNote.js';
import type { MandateRecord } from './types.js';
import {
  displayAgentName,
  extractCompatibleBotName,
  friendlyActionLabel,
  friendlyRejectReason,
  inferAgentFromUserAgent,
  RULE_STATE_LABEL,
  RULE_STATE_TOOLTIP,
  ruleResultState,
} from './intent-label.js';
import {
  Anchor,
  AdvisoryVerdictPill,
  composeHeadline,
  HandoffPanel,
  outcomePresentation,
  PayloadPanel,
  RefundBlock,
  RequestedLineItems,
  readHandoff,
  readOrderBreakdown,
  readOriginalOrder,
  readSettlement,
  RulesTrace,
  SettlementPanel,
  type NavToGuardrails,
  // (Chip-side adapter for the Security Attackers link.)
} from './MandateDetail.js';
import {
  fromSettlement,
  fromUcpBreakdown,
  OrderMoneyBreakdown,
  type OrderMoney,
} from './OrderMoneyBreakdown.js';
import {
  HEADER_COUNT_TOOLTIP,
  PROBE_BLOCK_HINT,
  PROTOCOL_TOOLTIP,
  SEEN_BY_TOOLTIP,
  identityCheckSentence,
  isSillToolVisit,
  impostorProbeLine,
  probeAlert,
  probeBorrowedNameNote,
  probeContext,
  probeFacts,
  probeRequestsLead,
  probeShowsBlockHint,
  protocolLabel,
  protocolTooltip,
  recordDerivedProtocol,
  seenByLabel,
  visitGroupName,
  isPersonGroupKey,
  isScannerGroupKey,
  groupConfirmedLabel,
  groupConfirmedNote,
  headerCountText,
  headerLine2Parts,
  sillToolPurpose,
  mismatchCallout,
  mismatchReason,
  quotedName,
  visitOperator,
  visitPurpose,
  isUnverifiedFlagged,
  hostileRegistryPurpose,
  type IdentityStateResult,
} from './visit-identity.js';
import { SecurityFlagChip } from './SecurityFlagChip.js';
import { VisitIdentityMark, VisitLogoGlyph } from './VisitIdentityMarks.js';
import { VisitTrustChip } from './VisitTrustChip.js';
import { AgentIdentitySplit } from './AgentIdentitySplit.js';
import { ProbeExamples } from './ProbingPill.js';
import { ScannerIpsSection } from './ScannerIpsSection.js';
import { BotAddressBlockSection } from './BotAddressBlockSection.js';
import { useRecordAddress } from './record-address.js';
import { DetailActionRow } from './DetailActionRow.js';
import { blockUnavailableReason, useIsShopifySite } from './BlockControl.js';
import { isUcpOrder, ucpPlatformAuth, ucpPlatformHost, isUcpSillBranded, type UcpPlatformAuth } from './ucp.js';
import { SillMark } from './SillMark.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type V3Tab = 'overview' | 'activity' | 'raw';

/** The narrow setView shape the SecurityFlagChip calls. The chip's own
 *  type uses the dashboard-wide SetView; this file only receives the
 *  narrow {@link NavToGuardrails}, so we adapt via `onOpenAttackers`. */
type ChipSetView = (view: 'security', opts?: { readonly threat?: string; readonly security_section?: string }) => void;

// ---------------------------------------------------------------------------
// Country code → flag emoji (unicode regional indicator letters)
// ---------------------------------------------------------------------------

function countryFlag(_iso2: string): string {
  return ''; // deprecated — use FlagImg component
}

function FlagImg({ code }: { code: string | undefined }): JSX.Element | null {
  if (!code || code.length !== 2) return null;
  return (
    <span style={{ display: 'inline-flex', marginRight: 6 }}>
      <CountryFlag code={code} withName={false} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Resolved agent name for the panel headline.
// ---------------------------------------------------------------------------

/**
 * Back-compat re-export: `isSillSignedAgent` is now `isSillCredentialSigner`
 * from `@sill/webbotauth`. Kept as a wrapper so prior external callers
 * do not break; new code should import `isSillCredentialSigner` directly.
 */
export function isSillSignedAgent(signed_agent: string | undefined): boolean {
  return isSillCredentialSigner(signed_agent ?? null);
}

/** Back-compat re-export: `firstProductTokenFromUa` is now
 *  `inferUaProductToken` from `@sill/types`. */
export function firstProductTokenFromUa(ua: string | undefined): string | null {
  return inferUaProductToken(ua ?? '');
}

/**
 * ADR-0234 — resolve the merchant-facing agent name for the panel
 * headline. ORDER matters:
 *
 *   1. A verified Web Bot Auth signature wins — the UA is install-
 *      reported and could be anything, so a bundled-registry UA match
 *      ("GPTBot" → `agent_openai_gpt`) must NOT become the headline
 *      when a verified signer is someone else (security review 2026-
 *      10-11 MAJOR 1). The signature branch therefore runs BEFORE the
 *      bundled-registry agent_id branch.
 *   2. If no signature, fall through to the pre-fix order (registry
 *      agent_id → inferred/compatible token → ai_summary.who →
 *      "Unidentified visitor").
 */
export function resolveAgentHeadline(record: AuditRecord): string {
  const agent_id =
    record.identification_match?.agent_id ?? record.rejected_agent_id;
  // ADR-0234 — signature branch FIRST (see docstring above).
  if (record.signature_verified === true) {
    const signer = (record.signed_agent ?? '').trim();
    const uaToken = inferUaProductToken(record.user_agent ?? '');
    if (isSillCredentialSigner(signer)) {
      // Case A — the operator registered this UA as one of its agents:
      // prefer the operator-chosen display name ("Sill Readiness") over
      // the raw token ("SillReadiness") so the merchant list reads what
      // the operator says the agent is called, not what the UA happens
      // to spell. Shared with `@sill/audit/labels.sillCredentialWhoLabel`.
      if (record.signed_credential_name_registered === true
          && typeof record.signed_credential_agent_display === 'string'
          && record.signed_credential_agent_display.length > 0) {
        return record.signed_credential_agent_display;
      }
      // Case B / enrichment-unavailable — show the UA token "as sent"
      // so the merchant can tell what the agent CLAIMED; the Identity
      // block carries the "name not registered" / "unconfirmed"
      // explanation. Never pass through to the bundled-registry
      // agent_id branch here (MAJOR 1 fix).
      if (uaToken !== null && uaToken.length > 0) return uaToken;
      if (typeof record.signed_operator_display === 'string'
          && record.signed_operator_display.length > 0) {
        return record.signed_operator_display;
      }
      if (typeof record.signed_operator_slug === 'string'
          && record.signed_operator_slug.length > 0) {
        return record.signed_operator_slug;
      }
      return 'Signed visit';
    }
    // Public signer: only trust the signer's merchant-facing name when
    // the UA token matches the signer's documented pattern
    // (KNOWN_PUBLIC_SIGNERS). Otherwise the signature was valid but the
    // UA is not the one this signer legitimately ships, so fall through
    // to the UA token or signer host (never the bundled-registry match).
    if (signer.length > 0) {
      const known = publicSignerDisplayFor(signer, record.user_agent);
      if (known !== null) return known;
      if (uaToken !== null) return uaToken;
      return signer;
    }
  }
  // Pre-fix order from here down.
  // A human visit: "Visitor from Pembroke Pines" (founder 2026-10-13), the
  // same name as the list row and the per-person rows.
  if (agent_id === undefined && isHumanVisit(record)) return humanRecordName(record);
  if (agent_id !== undefined) {
    const name = displayAgentName(agent_id);
    if (name !== agent_id) return name;
  }
  // A name the UA carries IS an identification — "Reflectionbot", not
  // "Unknown bot" with the name shown somewhere else (founder 2026-09-23).
  // Registry near-miss first (a known product on a UA the registry
  // pattern missed), then the `compatible; Name/1.0` token.
  const cls = record.identification_match?.visitor_class;
  if (cls !== 'human_likely') {
    const inferred = inferAgentFromUserAgent(record.user_agent);
    if (inferred !== null) {
      const name = displayAgentName(inferred).split(' · ').pop();
      if (name !== undefined && name.length > 0 && name !== inferred) return name;
    }
    const token = extractCompatibleBotName(record.user_agent);
    if (token !== null && token.length > 0) return token;
    // Last-resort: fall back to the plain first product token of the UA so
    // "SillReadiness/1.0" never reads as "Unidentified visitor" even on an
    // unsigned beacon.
    const uaToken = inferUaProductToken(record.user_agent ?? '');
    if (uaToken !== null && uaToken.length > 0) return uaToken;
  }
  const summary = record.ai_summary;
  if (summary !== undefined && summary.who.length > 0) {
    return summary.who.split('.')[0] ?? summary.who;
  }
  if (agent_id !== undefined) return agent_id;
  if (cls === 'human_likely') return 'Human visitor';
  return 'Unidentified visitor';
}

// ---------------------------------------------------------------------------
// Raw identifier line
// ---------------------------------------------------------------------------

function resolveRawIdentifier(record: AuditRecord): string | null {
  if (record.user_agent !== undefined && record.user_agent.length > 0) {
    return record.user_agent;
  }
  const agent_id = record.identification_match?.agent_id ?? record.rejected_agent_id;
  return agent_id ?? null;
}

// ---------------------------------------------------------------------------
// Inline copy button
// ---------------------------------------------------------------------------

function CopyButton({ value, C }: { value: string; C: ReturnType<typeof useC> }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onClick = (): void => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 1500);
    });
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Copy"
      style={{
        background: 'transparent',
        border: 'none',
        padding: '0 4px',
        cursor: 'pointer',
        color: C.muted,
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    >
      {copied ? <Check size={10} color={C.limeFg} /> : <Copy size={10} />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// 24h activity sparkline — buckets agentHistory into hourly bins.
// Renders as a 24px tall SVG polyline, single lime hue, no axes.
// ---------------------------------------------------------------------------

function ActivitySparkline({
  C,
  records,
}: {
  C: ReturnType<typeof useC>;
  records: AuditRecord[];
}): JSX.Element | null {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const nowRef = Date.now();
  const windowMs = 24 * 60 * 60 * 1000;
  const buckets = new Array<number>(24).fill(0);
  for (const r of records) {
    const age = nowRef - new Date(r.evaluated_at).getTime();
    if (age < 0 || age >= windowMs) continue;
    const bucket = Math.floor((age / windowMs) * 24);
    // bucket 0 = most recent hour; reverse so index 0 = oldest (left of chart)
    const idx = 23 - Math.min(bucket, 23);
    buckets[idx] = (buckets[idx] ?? 0) + 1;
  }

  const max = Math.max(...buckets, 1);
  if (max === 0) return null;

  const W = 280;
  const H = 40;
  const padX = 4;
  const padY = 6;
  const step = (W - padX * 2) / 23;

  // Pre-compute each point's pixel coords
  const pts = buckets.map((v, i) => ({
    x: padX + i * step,
    y: H - padY - ((v / max) * (H - padY * 2)),
    count: v,
    // Hour label: index 0 = 24h ago, index 23 = current hour
    hoursAgo: 23 - i,
  }));

  const polylineStr = pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  // Area fill path: down to baseline, back left along bottom
  const areaPath = [
    `M ${pts[0]!.x.toFixed(1)},${H}`,
    ...pts.map(p => `L ${p.x.toFixed(1)},${p.y.toFixed(1)}`),
    `L ${pts[23]!.x.toFixed(1)},${H}`,
    'Z',
  ].join(' ');

  const hovered = hoverIdx !== null ? (pts[hoverIdx] ?? null) : null;

  // Tooltip: position left-side for right-half hovers to stay in bounds
  const tooltipRight = hoverIdx !== null && hoverIdx > 16;

  const hourLabel = (p: typeof pts[0]): string => {
    if (p.hoursAgo === 0) return 'Now';
    if (p.hoursAgo === 1) return '1h ago';
    return `${p.hoursAgo}h ago`;
  };

  return (
    <div
      style={{ marginBottom: 12, position: 'relative', userSelect: 'none' }}
    >
      {/* Kicker */}
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 8,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        Activity · last 24h
      </div>

      <div style={{ position: 'relative' }}>
        <svg
          width="100%"
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          style={{ display: 'block', cursor: 'crosshair' }}
          onMouseLeave={() => { setHoverIdx(null); }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const mx = ((e.clientX - rect.left) / rect.width) * W;
            // Find nearest bucket by x position
            let nearest = 0;
            let nearestDist = Infinity;
            for (let i = 0; i < pts.length; i++) {
              const d = Math.abs((pts[i]?.x ?? 0) - mx);
              if (d < nearestDist) { nearestDist = d; nearest = i; }
            }
            setHoverIdx(nearest);
          }}
        >
          {/* Area fill */}
          <path
            d={areaPath}
            fill={C.limeDim}
            fillOpacity={1}
          />

          {/* Line */}
          <polyline
            points={polylineStr}
            fill="none"
            stroke={C.limeFg}
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.8}
          />

          {/* Hover vertical rule */}
          {hovered !== null ? (
            <line
              x1={hovered.x.toFixed(1)}
              y1={0}
              x2={hovered.x.toFixed(1)}
              y2={H}
              stroke={C.limeFg}
              strokeWidth="1"
              strokeDasharray="2 2"
              opacity={0.5}
            />
          ) : null}

          {/* Hover dot */}
          {hovered !== null ? (
            <circle
              cx={hovered.x.toFixed(1)}
              cy={hovered.y.toFixed(1)}
              r={3}
              fill={C.limeFg}
            />
          ) : null}

          {/* Hit area — invisible wide bars for easier hover targeting */}
          {pts.map((p, i) => (
            <rect
              key={i}
              x={p.x - step / 2}
              y={0}
              width={step}
              height={H}
              fill="transparent"
              onMouseEnter={() => { setHoverIdx(i); }}
            />
          ))}
        </svg>

        {/* Tooltip */}
        {hovered !== null ? (
          <div
            style={{
              position: 'absolute',
              top: 0,
              ...(tooltipRight
                ? { right: `${100 - ((pts[hoverIdx!]?.x ?? 0) / W) * 100}%` }
                : { left: `${((pts[hoverIdx!]?.x ?? 0) / W) * 100}%` }),
              transform: tooltipRight ? 'translateX(0)' : 'translateX(4px)',
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '3px 7px',
              fontFamily: monoFont,
              fontSize: 9,
              color: C.text,
              letterSpacing: 1,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <span style={{ color: C.limeFg, marginRight: 6 }}>
              {hovered.count.toLocaleString('en-US')}
            </span>
            {hovered.count === 1 ? 'request' : 'requests'}
            <span style={{ color: C.muted, marginLeft: 6 }}>
              {hourLabel(hovered)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Order money — adapter helpers used by the Order section
// ---------------------------------------------------------------------------

/**
 * Fold the two settled-money sources into one `OrderMoney` shape:
 *   1) `order_breakdown` (UCP checkout) — richest, carries titles,
 *      shipping method, tax place, per-line prices.
 *   2) `settlement` evidence — Stripe/Shopify rail payload.
 * Returns `null` on records that carry neither (observations, pending
 * discovery) so the caller can skip the section.
 *
 * Refund settlements return `null` here: the RefundBlock below is the
 * right surface for the refund money, and the OrderMoneyBreakdown
 * assumes a forward-order shape (positive line totals, positive total,
 * no `original_total_minor` smuggling). The Activity trace renders the
 * RefundBlock separately.
 */
function readOrderMoney(record: AuditRecord): OrderMoney | null {
  const ob = readOrderBreakdown(record);
  if (ob !== undefined) return fromUcpBreakdown(ob);
  const s = readSettlement(record);
  if (s === undefined) return null;
  if (s.kind === 'refund') return null;
  const has_lines = Array.isArray(s.line_items) && s.line_items.length > 0;
  const has_total = typeof s.total_minor === 'number';
  if (!has_lines && !has_total) return null;
  return fromSettlement(s);
}

/**
 * `true` when the Order section WILL render a per-line cart (either
 * from `order_breakdown` or from `settlement.line_items`). Drives the
 * Intent panel's hide-when-duplicate branch so a UCP order never shows
 * the same cart twice.
 */
function orderSectionWillRenderLines(record: AuditRecord): boolean {
  const ob = readOrderBreakdown(record);
  if (ob !== undefined) return ob.line_items.length > 0;
  const s = readSettlement(record);
  if (s === undefined || s.kind === 'refund') return false;
  return Array.isArray(s.line_items) && s.line_items.length > 0;
}

// ---------------------------------------------------------------------------
// UCP order detection + presentation helpers
// ---------------------------------------------------------------------------
//
// `isUcpOrder` and `ucpPlatformHost` live in `./ucp.ts` so the list row
// and the Activity trace can share them without pulling the whole detail
// module. Re-exported here so existing imports keep working.

export { isUcpOrder, ucpPlatformHost } from './ucp.js';

/**
 * Headline + subline for a UCP record's panel header. Replaces the
 * registry/UA-driven resolver so an agent checkout never reads as
 * "Unidentified visitor" (founder bug 2026-10-21). Staff-run test orders
 * are labelled "Sill test platform" explicitly.
 */
export function ucpPlatformHeader(record: AuditRecord): { name: string; subline: string } {
  const staff = (record as MandateRecord).staff_test === true;
  if (staff) {
    return { name: 'Sill test platform', subline: 'Agent checkout · UCP' };
  }
  const host = ucpPlatformHost(record);
  return {
    name: host ?? 'Agent platform',
    subline: 'Agent checkout · UCP',
  };
}

/**
 * Short derived payment status for a UCP order's Overview Payment row.
 * Reads the settled rail outcome + any payment reference and condenses
 * the merchant-facing truth into one sentence (never "0.00" when a
 * failure attempted a total; never a bare "Settled" for an escalation
 * that is still waiting on the platform). Returns `null` when there is
 * nothing to say (e.g. a plain discovery record).
 */
export function ucpPaymentSummary(
  record: AuditRecord,
): { label: string; reference: string | null; tone: 'approve' | 'warn' | 'reject' | 'neutral' } | null {
  const s = readSettlement(record);
  const ob = readOrderBreakdown(record);
  const d = record.decision;
  const held = d === 'escalated_approved' || d === 'escalated_rejected';
  const ref = s?.stripe_payment_intent_id ?? s?.stripe_charge_id ?? s?.shopify_order_id ?? null;
  const railLabel =
    s?.rail === 'stripe' ? 'Stripe'
    : s?.rail === 'shopify' ? 'Shopify'
    : null;
  const currency = s?.currency ?? ob?.currency ?? 'USD';
  const chargeAmount =
    typeof s?.total_minor === 'number' ? formatMinor(s.total_minor, currency)
    : typeof ob?.total_minor === 'number' ? formatMinor(ob.total_minor, currency)
    : null;

  if (d === 'rejected' || d === 'rejected_post_verify' || d === 'verification_rejected' || d === 'escalated_rejected') {
    return { label: 'Rejected', reference: null, tone: 'reject' };
  }

  if (s?.outcome === 'failed' || s?.outcome === 'aborted') {
    const reason = s.failure_reason ?? s.reason ?? null;
    const detail = typeof reason === 'string' && reason.length > 0
      ? reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      : 'payment did not clear';
    const attempted = chargeAmount !== null ? ` · attempted ${chargeAmount}` : '';
    return { label: `Payment failed · ${detail}${attempted}`, reference: ref, tone: 'reject' };
  }

  if (s?.outcome === 'refunded' || s?.kind === 'refund') {
    return {
      label: railLabel !== null && chargeAmount !== null
        ? `Refunded ${chargeAmount} · ${railLabel}`
        : 'Refunded',
      reference: ref,
      tone: 'warn',
    };
  }

  if (s?.outcome === 'succeeded') {
    const headlinePrefix = held ? 'Held by your rules → Approved → ' : '';
    const label = chargeAmount !== null && railLabel !== null
      ? `${headlinePrefix}Charged ${chargeAmount} · ${railLabel}`
      : chargeAmount !== null
        ? `${headlinePrefix}Charged ${chargeAmount}`
        : `${headlinePrefix}Charged`;
    return { label, reference: ref, tone: 'approve' };
  }

  // No settled rail evidence yet. If the policy approved (either path),
  // say so and wait on the platform. Otherwise nothing to show here.
  if (d === 'approved' || d === 'escalated_approved') {
    const base = held ? 'Held by your rules → Approved' : 'Approved';
    return { label: `${base} · waiting for the platform to complete payment`, reference: null, tone: 'warn' };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Section kicker label
// ---------------------------------------------------------------------------

function Kicker({ C, label }: { C: ReturnType<typeof useC>; label: string }): JSX.Element {
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        letterSpacing: 2,
        textTransform: 'uppercase',
        marginBottom: 8,
      }}
    >
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

function TabBar({
  C,
  value,
  onChange,
}: {
  C: ReturnType<typeof useC>;
  value: V3Tab;
  onChange: (tab: V3Tab) => void;
}): JSX.Element {
  const tabs: { key: V3Tab; label: string }[] = [
    { key: 'overview', label: 'OVERVIEW' },
    { key: 'activity', label: 'ACTIVITY' },
    { key: 'raw', label: 'RAW DATA' },
  ];
  return (
    <div
      className="flex items-center"
      style={{ borderBottom: `1px solid ${C.border}`, marginBottom: 16, gap: 0 }}
    >
      {tabs.map(({ key, label }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={active}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: active ? `2px solid ${C.limeFg}` : '2px solid transparent',
              padding: '8px 12px',
              fontFamily: monoFont,
              fontSize: 9,
              letterSpacing: 1.5,
              fontWeight: active ? 700 : 500,
              color: active ? C.text : C.muted,
              cursor: 'pointer',
              marginBottom: -1,
              textTransform: 'uppercase',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// InfoTip — the Key-facts info icon. A 24×24 tap target around an 11px
// glyph (spec §12.3) on the shared `Tooltip` atom, which replaced the old
// hand-rolled portal (left-anchored, no shift/flip, and onClick+onTouchStart
// double-toggle on touch).
// ---------------------------------------------------------------------------

function InfoTip({ text, C }: { text: string; C: ReturnType<typeof useC> }): JSX.Element {
  return (
    <Tooltip content={text} side="bottom" aria-label="More info" data-testid="keyfact-info" style={{ flexShrink: 0 }}>
      <span
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'help',
          color: C.muted,
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
          marginLeft: 4,
          flexShrink: 0,
          minWidth: 24,
          minHeight: 24,
          justifyContent: 'center',
        }}
      >
        <Info size={11} />
      </span>
    </Tooltip>
  );
}

/**
 * The description shown under the name in the detail header (founder
 * 2026-10-14): a Sill tool's merchant-facing purpose, else the AI
 * description of the bot (`ai_summary.who`). Never on Mismatch (it would
 * describe the real agent) or people.
 */
export function visitHeaderDescription(record: AuditRecord, result: IdentityStateResult, isPeople: boolean): string | null {
  if (isPeople || result.state === 'mismatch' || result.state === 'person') return null;
  const sill = sillToolPurpose(record);
  if (sill !== null) return sill;
  const who = record.ai_summary?.who;
  return typeof who === 'string' && who.trim().length > 0 ? who.trim() : null;
}

// KeyFactRow — a single label/value row for the Overview facts grid
// ---------------------------------------------------------------------------

function KeyFactRow({
  C,
  label,
  tooltip,
  children,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  tooltip?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3" style={{ minHeight: 24 }}>
      <dt
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          minWidth: 80,
          paddingTop: 2,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {label}
        {tooltip !== undefined ? <InfoTip text={tooltip} C={C} /> : null}
      </dt>
      <dd
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.text,
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        {children}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cross-merchant network stat tiles (agent panel Phase C)
// ---------------------------------------------------------------------------

interface AgentNetworkStats {
  sites_seen: number | null;
  hit_count: number | null;
  first_seen_at: string | null;
  refusal_rate: number | null;
  dispute_rate: number | null;
  /** ADR-0210 Slice 4 — share of network traffic carrying a spoof signal. */
  spoof_rate?: number | null;
  report_count: number;
  reported_by_me: boolean;
}

function relativeAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days < 1) return 'today';
  if (days < 7) return `${days} day${days === 1 ? '' : 's'}`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return `${weeks} wk`;
  return `${Math.floor(days / 30)} mo`;
}

function StatTile({
  C,
  value,
  label,
  accent,
  color,
}: {
  C: ReturnType<typeof useC>;
  value: string | null;
  label: string;
  accent?: boolean;
  color?: string;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 20,
          fontWeight: 700,
          color: value !== null ? (color ?? (accent ? C.limeFg : C.text)) : C.muted,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
        }}
      >
        {value ?? '—'}
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 8,
          color: C.muted,
          letterSpacing: 1.5,
          marginTop: 3,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Friendly protocol label
// ---------------------------------------------------------------------------

/**
 * Visits panel Step C ("honest protocol"): `inbound_protocol` says how the
 * agent talked to the site (`'embed'` = an ordinary page request →
 * "Web (HTTP)"; `'mcp'` / `'a2a'` / `'ap2'` only via Sill's agent
 * endpoints). Where the visit was recorded (script tag, Cloudflare Worker,
 * WordPress plugin, server-side install; ADR-0211 `via`) is a separate
 * "Seen by" row (`seenByLabel`). Both live in `visit-identity.ts`.
 */
export { protocolLabel, seenByLabel } from './visit-identity.js';

// ---------------------------------------------------------------------------
// Report to network button — quiet neutral action (labels-v2: no amber on a
// visit without server evidence), optimistic flip
// ---------------------------------------------------------------------------

function ReportToNetworkButton({
  C,
  agent_key,
  initial_reported,
}: {
  C: ReturnType<typeof useC>;
  agent_key: string;
  initial_reported: boolean;
}): JSX.Element | null {
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const [reported, setReported] = useState(initial_reported);

  useEffect(() => { setReported(initial_reported); }, [initial_reported]);

  // Server-side the report route is owner/admin only (2026-10-02
  // security review). Hide the button entirely for reviewer / viewer;
  // legacy test scaffolds that mount without `<AuthProvider>` get a
  // null auth and keep the pre-existing behaviour (button visible).
  const role =
    auth !== null && auth.state.kind === 'authenticated'
      ? auth.state.user.role
      : null;
  const canReport =
    role === null || role === 'owner' || role === 'admin';
  if (!canReport) return null;

  const onReport = (): void => {
    if (http === null || reported) return;
    setReported(true);
    void http.request({ method: 'POST', path: `/v1/bots/${encodeURIComponent(agent_key)}/report` })
      .catch(() => { /* best-effort */ });
  };

  return (
    <div className="mb-5">
      <button
        type="button"
        onClick={onReport}
        disabled={reported || http === null}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          padding: '8px 12px',
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          fontFamily: monoFont,
          fontSize: 11,
          letterSpacing: 1,
          textTransform: 'uppercase' as const,
          color: C.muted,
          cursor: reported ? 'default' : 'pointer',
        }}
      >
        {reported ? (
          <><Check size={11} /> Reported to network</>
        ) : (
          <><Flag size={11} /> Report to network</>
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UCP Overview blocks — Payment · Ship to · Placed by.
//
// Rendered after the shared Order money block (which already carries the
// cart + totals). Keeps three short rows readable at a glance: what
// cleared, where it ships, and who placed it. All data is projected on
// the server — the component is a thin presentation of the audit record +
// `order_breakdown` the single-record route delivers.
// ---------------------------------------------------------------------------

/**
 * Compact one-line settled meta row: `SETTLED · Stripe · USD · pi_…`
 * (status · rail · currency · reference). Rendered under the Payment
 * sentence on UCP orders, so a merchant reads one card instead of
 * flipping between Payment + Settlement status.
 *
 * The reference is click-to-copy (mono, muted, breaks on wrap). Omits
 * any field we don't have — `rail` and `currency` are read from the
 * settlement evidence; `reference` from the strongest id available.
 * Returns `null` when none of the four parts are present (a UCP
 * approval that hasn't settled yet).
 */
function UcpPaymentSettledMeta({
  C,
  record,
}: {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
}): JSX.Element | null {
  const s = readSettlement(record);
  if (s === undefined) return null;
  const isRefund = s.kind === 'refund';
  const isFailed = s.outcome === 'failed' || s.outcome === 'aborted';
  const status = isRefund
    ? (s.refunded_minor !== undefined
        && s.original_total_minor !== undefined
        && s.refunded_minor >= s.original_total_minor
        ? 'REFUNDED'
        : 'PARTIAL REFUND')
    : isFailed
      ? (s.outcome === 'aborted' ? 'ABORTED' : 'FAILED')
      : s.outcome === 'refunded'
        ? 'REFUNDED'
        : s.outcome === 'succeeded'
          ? 'SETTLED'
          : null;
  const railLabel = s.rail !== undefined ? s.rail.toUpperCase() : null;
  const currency = s.currency !== undefined && s.currency.length > 0 ? s.currency.toUpperCase() : null;
  const ref = s.stripe_payment_intent_id
    ?? s.stripe_charge_id
    ?? s.stripe_refund_id
    ?? s.shopify_order_id
    ?? null;
  if (status === null && railLabel === null && currency === null && ref === null) return null;
  const copyRef = (): void => {
    if (ref === null) return;
    try { void navigator.clipboard.writeText(ref); } catch { /* clipboard blocked */ }
  };
  return (
    <div
      data-testid="ucp-payment-settled-meta"
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        color: C.muted,
        letterSpacing: 1,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        columnGap: 8,
        rowGap: 2,
        overflowWrap: 'anywhere',
      }}
    >
      {status !== null ? <span>{status}</span> : null}
      {railLabel !== null ? <span>· {railLabel}</span> : null}
      {currency !== null ? <span>· {currency}</span> : null}
      {ref !== null ? (
        // Separator stays plain; only the reference id itself is the
        // copy-link affordance (founder 2026-10-21: the underline used to
        // reach back over the " · ", which read as "copy that too").
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, minWidth: 0 }}>
          <span>·</span>
          <Tooltip content="Click to copy">
            <button
              type="button"
              onClick={copyRef}
              data-testid="ucp-payment-settled-ref"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                cursor: 'pointer',
                wordBreak: 'break-all',
                textAlign: 'left',
                textDecoration: 'underline',
                textUnderlineOffset: 2,
              }}
            >
              {ref}
            </button>
          </Tooltip>
        </span>
      ) : null}
    </div>
  );
}

/**
 * One compact refund row — mint RELEASED pill + the released amount and
 * (where known) the Stripe refund id. Rendered inside the Payment card
 * for UCP orders when the record carries a refund settlement, so a
 * merchant does not have to flip to a separate Settlement block.
 */
function UcpPaymentRefundMeta({
  C,
  record,
}: {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
}): JSX.Element | null {
  const s = readSettlement(record);
  if (s === undefined || s.kind !== 'refund') return null;
  const amount = typeof s.refunded_minor === 'number'
    ? formatMinor(s.refunded_minor, s.currency ?? 'USD')
    : null;
  const refundId = s.stripe_refund_id ?? null;
  return (
    <div
      data-testid="ucp-payment-refund-meta"
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        color: C.muted,
        letterSpacing: 1,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        columnGap: 8,
        rowGap: 2,
      }}
    >
      <span style={{ color: C.coralFg ?? '#FF6B47' }}>REFUND</span>
      {amount !== null ? <span>· {amount}</span> : null}
      {refundId !== null ? <span style={{ wordBreak: 'break-all' }}>· {refundId}</span> : null}
    </div>
  );
}

export function UcpOrderDetailBlocks({
  C,
  record,
}: {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
}): JSX.Element {
  const payment = ucpPaymentSummary(record);
  const ob = readOrderBreakdown(record);
  const ship = ob?.ship_to ?? null;
  const ucpHeader = ucpPlatformHeader(record);
  const host = ucpPlatformHost(record);
  const paymentColor =
    payment === null ? C.text
    : payment.tone === 'approve' ? (C.mintFg ?? C.limeFg)
    : payment.tone === 'reject' ? (C.coralFg ?? '#FF6B47')
    : payment.tone === 'warn' ? (C.amberFg ?? '#FFB627')
    : C.text;

  return (
    <>
      {payment !== null ? (
        <div className="mb-5" data-testid="ucp-payment-block">
          <Kicker C={C} label="Payment" />
          <div
            className="p-3 space-y-1"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: paymentColor,
                lineHeight: 1.5,
                overflowWrap: 'anywhere',
              }}
            >
              {payment.label}
            </div>
            {/* One meta line: SETTLED · Stripe · USD · pi_… (copyable).
                Supersedes the separate "Settlement status" block for UCP
                orders — reads top-to-bottom in one card. */}
            <UcpPaymentSettledMeta C={C} record={record} />
            {/* Refund / dispute rows stay in Payment for UCP so no evidence
                is lost after merging Settlement status in. */}
            <UcpPaymentRefundMeta C={C} record={record} />
          </div>
        </div>
      ) : null}

      {ship !== null ? (
        <div className="mb-5" data-testid="ucp-ship-to-block">
          <Kicker C={C} label="Ship to" />
          <div
            className="p-3 flex items-center gap-2"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              overflowWrap: 'anywhere',
            }}
          >
            <CountryFlag code={ship.country} size={16} />
            <span>
              {ship.region !== null && ship.region.length > 0 ? `${ship.region}, ` : ''}
              {countryName(ship.country) ?? ship.country}
            </span>
          </div>
          <div
            data-testid="ucp-ship-to-note"
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.muted,
              lineHeight: 1.5,
              marginTop: 6,
            }}
          >
            {/* Overview: Buyer detail lives on the Activity tab (one
                record per hour). The Activity-tab copy of this block uses
                a different note that points down to Buyer detail below. */}
            Full address is under Activity → Buyer detail (reveal is logged).
          </div>
        </div>
      ) : null}

      <div className="mb-5" data-testid="ucp-placed-by-block">
        <Kicker C={C} label="Placed by" />
        <div
          className="p-3"
          style={{
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          {/* Platform name (bold) + host (muted mono) stack. The trust
              chip is NOT duplicated here — it lives in the header once;
              the one plain line below carries the same meaning without
              another visible pill. */}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                fontWeight: 600,
                overflowWrap: 'anywhere',
              }}
            >
              {ucpHeader.name}
            </div>
            {host !== null && host !== ucpHeader.name ? (
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  wordBreak: 'break-all',
                  marginTop: 2,
                }}
              >
                {host}
              </div>
            ) : null}
          </div>
          <div
            data-testid="ucp-placed-by-trust-line"
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.muted,
              lineHeight: 1.5,
              marginTop: 6,
            }}
          >
            {ucpPlatformAuth(record) === 'api_key'
              ? 'Signed in with the access key you created.'
              : ucpPlatformAuth(record) === 'signature'
                ? 'Requests signed and verified.'
                : 'Platform checked before the order was accepted.'}
          </div>
        </div>
        {/* Visitor IP is never meaningful on an agent-platform checkout —
            the platform places orders from its own servers. We say so
            explicitly so a merchant looking for the usual Location row
            understands why it isn't here. */}
        <div
          data-testid="ucp-no-buyer-ip-note"
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            marginTop: 6,
          }}
        >
          Buyer IP: not shared. Agent platforms place orders from their own
          servers, so location comes from the ship-to address.
        </div>
      </div>
    </>
  );
}

/**
 * Compact UCP Payment card for an Activity-trace event. Mirrors the
 * Overview Payment card (status sentence + `SETTLED · Rail · Currency ·
 * pi_…` meta line + refund meta where present) so a merchant reads the
 * same shape on both tabs. Replaces the per-event `SettlementPanel` for
 * UCP records so the trace doesn't carry two copies of the same money
 * evidence (ADR-0063 panel's Money breakdown is already above via the
 * UCP breakdown).
 */
function UcpEventPaymentBlock({
  C,
  record,
}: {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
}): JSX.Element | null {
  const payment = ucpPaymentSummary(record);
  if (payment === null) return null;
  const paymentColor =
    payment.tone === 'approve' ? (C.mintFg ?? C.limeFg)
    : payment.tone === 'reject' ? (C.coralFg ?? '#FF6B47')
    : payment.tone === 'warn' ? (C.amberFg ?? '#FFB627')
    : C.text;
  return (
    <div className="mb-4" data-testid="event-ucp-payment-block">
      <Kicker C={C} label="Payment" />
      <div
        className="p-3 space-y-1"
        style={{
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
      >
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: paymentColor,
            lineHeight: 1.5,
            overflowWrap: 'anywhere',
          }}
        >
          {payment.label}
        </div>
        <UcpPaymentSettledMeta C={C} record={record} />
        <UcpPaymentRefundMeta C={C} record={record} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab — curated key facts, not the technical DetailsGrid
// ---------------------------------------------------------------------------

export function OverviewTab({
  C,
  record,
  setView,
  onViewBotProfile,
  agentEventCount,
  agentActionCount,
  agentFirstSeen,
  networkStats,
  agentHistory,
  identityResult,
  groupVisits,
  groupLastSeen,
  eventsAreRecords,
  probeNote,
  headerDescription,
  recordAddress,
}: {
  C: ReturnType<typeof useC>;
  /** Text already shown under the name: an identical Purpose row is dropped. */
  headerDescription?: string | null;
  /** Founder 2026-10-19 — the visit's IP, shown in the Location row (reviewer+). */
  recordAddress?: string | null;
  /** Quiet note for a registry-named row whose visits requested probe paths. */
  probeNote?: string;
  record: AuditRecord;
  identityResult?: IdentityStateResult;
  /** Step B: the grouped row's visits over the range (the VISITS tile). */
  groupVisits?: number;
  /** Step B: the grouped row's newest visit (the Last seen fact). */
  groupLastSeen?: string;
  /** Step B: `agentEventCount` counts the row's audit records, not visits. */
  eventsAreRecords?: boolean;
  setView?: NavToGuardrails;
  onViewBotProfile?: (agentKey: string, displayName: string) => void;
  agentEventCount?: number;
  agentActionCount?: number;
  agentFirstSeen?: string;
  networkStats?: AgentNetworkStats | null;
  agentHistory?: AuditRecord[];
}): JSX.Element {
  const timeZone = useDisplayTimeZone();
  const is_discovery = record.decision === 'observed';
  const visit_count = (record as MandateRecord).visit_count;
  const agent_id = record.identification_match?.agent_id ?? record.rejected_agent_id;
  // ADR-0229 §9 — network counters and reports are keyed by the UA hash,
  // so on a Mismatch visit they describe the REAL agent whose name this
  // sender used. Hide them there until they are keyed on the mismatch.
  const isMismatch = identityResult?.state === 'mismatch';
  const netStats = isMismatch ? null : networkStats;

  // Top pages / search terms computed from agent history
  const topPages = (agentHistory !== undefined && agentHistory.length > 0) ? (() => {
    const counts = new Map<string, number>();
    for (const h of agentHistory) {
      if (h.page_path !== undefined) {
        counts.set(h.page_path, (counts.get(h.page_path) ?? 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([path, count]) => ({ path, count }));
  })() : [];

  const topSearches = (agentHistory !== undefined && agentHistory.length > 0) ? (() => {
    const counts = new Map<string, number>();
    const SEARCH_KEYS_AGG = ['q', 'search', 'query'];
    for (const h of agentHistory) {
      if (h.page_query !== undefined) {
        try {
          const params = new URLSearchParams(h.page_query.replace(/^\?/, ''));
          for (const k of SEARCH_KEYS_AGG) {
            const v = params.get(k);
            if (v !== null && v.trim().length > 0) { counts.set(v.trim(), (counts.get(v.trim()) ?? 0) + 1); break; }
          }
        } catch { /* ignore */ }
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([term, count]) => ({ term, count }));
  })() : [];

  // Location
  const geo_parts = [record.geo_city, record.geo_region].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  );
  const geo_country_name = countryName(record.geo_country) ?? record.geo_country ?? '';
  const geo_label =
    geo_parts.length > 0
      ? `${geo_parts.join(', ')}, ${geo_country_name}`
      : (record.geo_country !== undefined ? geo_country_name : null);

  // Purpose: Sill's own tools and server signature facts first, then the
  // AI description of the bot (`ai_summary.who`, the useful sentence the
  // founder asked to keep, 2026-10-14), then the bundled registry
  // category, then visitor_class.
  //
  // ADR-0234 — a verified Sill-credential signature outranks the
  // bundled-registry classifier. A Case A visit (operator approved a UA
  // token = this UA) reads as "Registered AI agent from <operator>" even
  // when the bundled registry does not carry the token; a Case B visit
  // (verified signature, UA token NOT registered to the operator) reads
  // as "Signed by <operator>; name is not registered to that operator"
  // instead of a bare "Unregistered bot".
  const purpose: string | null = (() => {
    // Sill's own tools: the registry sentence, never "registered AI agent".
    const sill_purpose = sillToolPurpose(record);
    if (sill_purpose !== null) return sill_purpose;
    // Founder bug 2026-10-20 — a flagged, unverified UA must not surface
    // the AI-written "who" (which can read benignly: "a WordPress
    // security vulnerability scanner"). Prefer the registry's hostile
    // category label when known, else a neutral purpose.
    if (isUnverifiedFlagged(record)) {
      const hostile = hostileRegistryPurpose(record);
      if (hostile !== null) return hostile;
      return 'Unverified sender';
    }
    if (record.signature_verified === true && isSillSignedAgent(record.signed_agent)) {
      const op = (typeof record.signed_operator_display === 'string' && record.signed_operator_display.length > 0)
        ? record.signed_operator_display
        : (typeof record.signed_operator_slug === 'string' && record.signed_operator_slug.length > 0
          ? record.signed_operator_slug
          : 'a Sill-enrolled operator');
      if (record.signed_credential_name_registered === true) {
        const agent = typeof record.signed_credential_agent_display === 'string'
          && record.signed_credential_agent_display.length > 0
          ? record.signed_credential_agent_display
          : null;
        return agent !== null
          ? `${agent} — registered AI agent from ${op}`
          : `Registered AI agent from ${op}`;
      }
      if (record.signed_credential_name_registered === false) return `Signed by ${op}; user-agent name is not registered to that operator`;
      return `Signed by ${op}`;
    }
    const who = record.ai_summary?.who;
    if (typeof who === 'string' && who.trim().length > 0) return who.trim();
    const cls = record.identification_match?.visitor_class;
    if (cls !== 'human_likely') {
      const registryPurpose = visitPurpose(record);
      if (registryPurpose !== 'Bot') return registryPurpose;
    }
    if (cls === 'matched_agent') return 'Registered AI agent';
    if (cls === 'unknown_agent') return 'Bot or crawler';
    if (cls === 'human_likely') return 'Human browser session';
    return null;
  })();

  // Action label
  const skill_id = record.skill_id ?? record.connector_call?.skill_id;
  const action_label =
    skill_id !== undefined ? friendlyActionLabel(skill_id) : null;

  // Policy
  const policy_label = record.policy_version !== undefined && record.policy_version.length > 0
    ? shortPolicyLabel(record.policy_version)
    : null;

  // Reject reason
  const reject_reason = record.reject_reason !== undefined
    ? friendlyRejectReason(record.reject_reason)
    : null;

  // ops2026 UCP checkout (2026-10-21) — a UCP order record is not a
  // visitor-identification row. The "1 VISITS" stats tile, the Agent-
  // profile cross-link and the visitor Key-facts set (Purpose /
  // Location / Page visited / Protocol / Seen by) are suppressed; the
  // dedicated Order / Payment / Ship to / Placed by sections carry the
  // story. The remaining three facts (Policy, Mandate ID, Time) move
  // under the heading "Details" and are placed AFTER Rules so money
  // reads first and reference data last.
  const isUcp = isUcpOrder(record);

  return (
    <div>
      {/* Stats bar — VISITS · ACTIONS · MERCHANTS SEEN ON (3 tiles max).
          Suppressed on UCP orders: "1 VISITS" is noise next to a cart. */}
      {isUcp ? null : (
      <div
        className="flex items-center gap-5 p-3 mb-5"
        style={{
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
      >
        <StatTile
          C={C}
          value={(groupVisits ?? (visit_count !== undefined ? visit_count : agentEventCount ?? 1)).toLocaleString()}
          label="VISITS"
        />
        {agentActionCount !== undefined && agentActionCount > 0 ? (
          <StatTile C={C} value={agentActionCount.toLocaleString()} label="ACTIONS" accent />
        ) : null}
        {netStats !== undefined && netStats !== null && netStats.sites_seen !== null && netStats.sites_seen > 0 ? (
          <StatTile C={C} value={netStats.sites_seen.toLocaleString()} label="MERCHANTS SEEN ON" />
        ) : null}
        {netStats !== undefined && netStats !== null && netStats.report_count > 0 ? (
          <StatTile C={C} value={netStats.report_count.toLocaleString()} label="MERCHANTS FLAGGED" color={C.amberFg ?? '#FFB627'} />
        ) : null}
        {/* ADR-0210 Slice 4 — network refusal rate across all merchants (30d). */}
        {netStats !== undefined && netStats !== null && netStats.refusal_rate !== null ? (
          <StatTile
            C={C}
            value={`${Math.round(netStats.refusal_rate * 100)}%`}
            label="REFUSED · NETWORK"
            {...(netStats.refusal_rate >= 0.2
              ? { color: netStats.refusal_rate >= 0.5 ? (C.coralFg ?? '#FF6B47') : (C.amberFg ?? '#FFB627') }
              : {})}
          />
        ) : null}
      </div>
      )}

      {/* Bot profile link — filter mandates to show all activity for this agent */}
      {/* Hidden on Mismatch: agent_key is the UA hash, so the profile is the
          real agent's (ADR-0229 §9). */}
      {/* UCP orders: "View all activity for this agent" would open the
          agent-platform's bot profile, not the merchant-side view of
          this order. Suppressed on UCP, like the stats bar. */}
      {record.agent_key !== undefined && onViewBotProfile !== undefined && !isMismatch && !isUcp ? (
        <div style={{ textAlign: 'right', marginBottom: 12, marginTop: -8 }}>
          <button
            type="button"
            onClick={() => { onViewBotProfile(record.agent_key!, resolveAgentHeadline(record)); }}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            View all activity for this agent →
          </button>
        </div>
      ) : null}

      {/* Mandate intent summary (transactional) — up top so line items
          are the first thing a merchant sees. HIDDEN when the Order
          section below renders its own per-line breakdown (either from
          the UCP checkout breakdown or the per-line settlement
          evidence); showing both would be a duplicate cart. */}
      {!is_discovery
        && record.intent_summary !== undefined
        && !orderSectionWillRenderLines(record) ? (
        <div className="mb-5">
          <Kicker C={C} label="Intent" />
          <RequestedLineItems
            C={C}
            intent={record.intent_summary}
            settlementLines={readSettlement(record)?.line_items}
          />
        </div>
      ) : null}

      {/* Key facts grid */}
      {/* Materialised as a variable so UCP orders can slot it in AFTER
          Rules (reference data last) under the heading "Details".
          Non-UCP records render it here, inline, as before. */}
      {(() => {
        // Pre-compute visibility for separator logic
        // Mismatch: the registry purpose describes the real agent, not this sender.
        //
        // UCP orders trim the grid to the three rows a merchant uses on
        // an agent order: Policy · Mandate ID · Last seen. The visitor-
        // level fields (Purpose, Location, Page visited, Search term,
        // Action, Protocol, Seen by, Buyer-presence) belong to the
        // visitor-identification model and read as noise next to the
        // dedicated Order / Payment / Ship to / Placed by sections.
        const ucpTrim = isUcpOrder(record);
        if (ucpTrim) return null; // slotted in later as "Details"
        const hasPurpose = !ucpTrim && purpose !== null && !isMismatch && purpose !== headerDescription;
        const hasLocation = !ucpTrim && (geo_label !== null || (recordAddress ?? null) !== null);
        const hasPage = !ucpTrim && record.page_path !== undefined && record.page_path.length > 0;
        const hasAction = !ucpTrim && (action_label !== null || is_discovery);
        // ADR-0253 §A — ALWAYS show a Protocol row (except on the UCP-trim
        // branch, where the panel renders a dedicated Order block). The
        // server projects `derived_protocol` via the shared SQL fold, so
        // rows with a NULL `inbound_protocol` column still render the
        // right label. Legacy pre-ADR-0253-§A responses that lack
        // `derived_protocol` fall back to the raw column; absence of
        // both reads as `'unknown'` (the dashboard shows "Unknown" — the
        // row is still present).
        const derivedProtocol = recordDerivedProtocol(record);
        const hasProtocol = !ucpTrim;
        const seenBy = ucpTrim ? null : seenByLabel(record);
        const hasPolicy = policy_label !== null;
        const hasReason = !ucpTrim && reject_reason !== null;

        const SEARCH_KEYS = ['q', 's', 'search', 'query', 'keyword', '_search'];
        let searchTerm: string | null = null;
        if (record.page_query !== undefined && record.page_query.length > 0) {
          try {
            const params = new URLSearchParams(
              record.page_query.startsWith('?') ? record.page_query.slice(1) : record.page_query,
            );
            for (const k of SEARCH_KEYS) {
              const v = params.get(k);
              if (v !== null && v.trim().length > 0) { searchTerm = v.trim(); break; }
            }
          } catch { /* malformed query string */ }
        }
        const hasSearch = searchTerm !== null;

        const hasLocationGroup = hasLocation || hasPage || hasSearch;
        const hasActionGroup = hasAction || hasProtocol || seenBy !== null || hasPolicy;

        const sep = (
          <hr style={{ border: 'none', borderTop: '1px solid', borderColor: C.borderSoft ?? C.border, margin: '10px 0' }} />
        );

        return (
          <div className="mb-5">
            <Kicker C={C} label="Key facts" />
            <dl className="space-y-3">
              {hasPurpose ? (
                <KeyFactRow C={C} label="Purpose" tooltip="What this agent is and why it visits — classified by AI from its user-agent and observed behavior.">
                  <span style={{ lineHeight: 1.5 }}>{purpose}</span>
                </KeyFactRow>
              ) : null}
              {hasPurpose && hasLocationGroup ? sep : null}
              {hasLocation ? (
                <KeyFactRow C={C} label="Location" tooltip="Country and region detected from the visitor's IP address at request time.">
                  <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                    {geo_label !== null ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <FlagImg code={record.geo_country} />
                        {geo_label}
                      </span>
                    ) : null}
                    {recordAddress != null ? (
                      <span data-testid="key-fact-ip" style={{ fontFamily: monoFont, fontSize: 11, color: C.text, overflowWrap: 'anywhere' }}>
                        {recordAddress}
                      </span>
                    ) : null}
                  </span>
                </KeyFactRow>
              ) : null}
              {hasPage ? (
                <KeyFactRow C={C} label="Page visited" tooltip="The URL path on your site that triggered this record.">
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      color: C.text,
                      wordBreak: 'break-all',
                    }}
                  >
                    {record.page_path}
                  </span>
                </KeyFactRow>
              ) : null}
              {hasSearch ? (
                <KeyFactRow C={C} label="Search term" tooltip="Search keyword extracted from the page's query string — what the agent was looking for on your site.">
                  <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>{searchTerm}</span>
                </KeyFactRow>
              ) : null}
              {hasLocationGroup && hasActionGroup ? sep : null}
              {action_label !== null ? (
                <KeyFactRow
                  C={C}
                  label="Action"
                  tooltip="The specific capability the agent requested — e.g. placing an order, checking inventory, or initiating a refund."
                >
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      background: C.surface2,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      padding: '2px 6px',
                    }}
                  >
                    {action_label}
                  </span>
                </KeyFactRow>
              ) : is_discovery ? (
                <KeyFactRow
                  C={C}
                  label="Action"
                  tooltip="The specific capability the agent requested — e.g. placing an order, checking inventory, or initiating a refund."
                >
                  <span style={{ color: C.muted }}>Discovery visit</span>
                </KeyFactRow>
              ) : null}
              {hasProtocol ? (
                <KeyFactRow
                  C={C}
                  label="Protocol"
                  tooltip={protocolTooltip(derivedProtocol)}
                >
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      background: C.surface2,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      padding: '2px 6px',
                    }}
                  >
                    {protocolLabel(derivedProtocol)}
                  </span>
                </KeyFactRow>
              ) : null}
              {record.a2a_task_id !== undefined && record.a2a_task_id.length > 0 ? (
                <KeyFactRow
                  C={C}
                  label="A2A task"
                  tooltip="The A2A Task id this message was dispatched from. The agent received the same id in the task envelope so merchant and agent can correlate the call."
                >
                  <span
                    data-testid="key-fact-a2a-task"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      color: C.text,
                      wordBreak: 'break-all',
                    }}
                  >
                    {record.a2a_task_id}
                  </span>
                </KeyFactRow>
              ) : null}
              {seenBy !== null ? (
                <KeyFactRow C={C} label="Seen by" tooltip={SEEN_BY_TOOLTIP}>
                  <span data-testid="key-fact-seen-by" style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>{seenBy}</span>
                </KeyFactRow>
              ) : null}
              {hasPolicy ? (
                <KeyFactRow
                  C={C}
                  label="Policy"
                  tooltip={`The rules on your Rules page that checked this request. Version counts how many times they have been published. Exact id in the signed record: ${record.policy_version ?? ''}`}
                >
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      background: C.surface2,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      padding: '2px 6px',
                    }}
                  >
                    {policy_label}
                  </span>
                </KeyFactRow>
              ) : null}
              {hasReason ? sep : null}
              {hasReason ? (
                <KeyFactRow C={C} label="Reason" tooltip="Why this request was rejected or escalated — the specific policy rule that fired.">
                  <span style={{ color: C.coralFg ?? C.text }}>{reject_reason}</span>
                </KeyFactRow>
              ) : null}
              {record.mandate_id !== undefined ? (
                <KeyFactRow C={C} label="Mandate ID" tooltip="The signed mandate reference for this transaction. Click to copy — use this ID when contacting support.">
                  <Tooltip content="Click to copy mandate ID">
                  <button
                    type="button"
                    onClick={() => { try { void navigator.clipboard.writeText(record.mandate_id!); } catch { /* ignore */ } }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.muted,
                      cursor: 'pointer',
                      wordBreak: 'break-all',
                      textAlign: 'left',
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                    }}
                  >
                    {record.mandate_id}
                  </button>
                  </Tooltip>
                </KeyFactRow>
              ) : null}
              {!ucpTrim && agentEventCount !== undefined && agentEventCount > 1 ? (
                <KeyFactRow
                  C={C}
                  label={eventsAreRecords === true ? 'Records' : 'Total events'}
                  tooltip={eventsAreRecords === true
                    ? 'Signed audit records behind this row. Sill keeps one record per agent per hour, so one record can hold many visits.'
                    : 'Total visits and interactions this agent has made on your site in the tracked history window.'}
                >
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {agentEventCount.toLocaleString()}
                  </span>
                </KeyFactRow>
              ) : null}
              {!ucpTrim && agentFirstSeen !== undefined && agentFirstSeen !== record.evaluated_at ? (
                <KeyFactRow C={C} label="First seen" tooltip="When this agent first appeared in your audit log.">
                  {formatIsoDate(agentFirstSeen, timeZone)}
                </KeyFactRow>
              ) : null}
              <KeyFactRow C={C} label={ucpTrim ? 'Time' : 'Last seen'} tooltip={groupLastSeen !== undefined ? 'The newest visit in this row.' : 'The timestamp of this specific audit record.'}>
                {formatIsoDate(groupLastSeen ?? record.evaluated_at, timeZone)}
              </KeyFactRow>
              {/* Buyer presence indicator — for settled/refunded records only, no PII.
                  UCP orders already route the buyer through Ship to / Buyer
                  detail below, so the duplicate row is suppressed. */}
              {!ucpTrim ? (() => {
                const s = readSettlement(record);
                if (s === undefined) return null;
                const settled = s.outcome === 'succeeded' || s.outcome === 'refunded' || s.kind === 'refund';
                const hasRef = s.shopify_order_id !== undefined || s.stripe_payment_intent_id !== undefined || s.stripe_charge_id !== undefined;
                if (!settled || !hasRef) return null;
                return (
                  <KeyFactRow C={C} label="Buyer" tooltip="A buyer record was captured for this transaction. Full details are in the Activity tab.">
                    <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>On file — see Activity tab</span>
                  </KeyFactRow>
                );
              })() : null}
            </dl>
            {probeNote !== undefined ? (
              <div
                data-testid="visit-probe-name-note"
                style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5, marginTop: 12 }}
              >
                {probeNote}
              </div>
            ) : null}
          </div>
        );
      })()}


      {/* Visits panel Step A (labels-v2): the AI Block/Monitor
          recommendation box is removed. AI never judges a visitor. */}

      {/* Connector call — compact result when a wired connector was dispatched */}
      {(() => {
        const cc = record.connector_call;
        if (cc === undefined || cc.kind === 'unwired' || cc.kind === 'pending_integration') return null;
        return (
          <div className="mb-5">
            <Kicker C={C} label="Connector call" />
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 2, padding: '8px 12px', background: C.surface2 }}>
              <div className="flex items-center gap-2 flex-wrap">
                {cc.platform !== 'none' ? (
                  <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.2 }}>
                    {cc.platform.toUpperCase()}
                  </span>
                ) : null}
                {cc.skill_id !== undefined ? (
                  <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>
                    {friendlyActionLabel(cc.skill_id)}
                  </span>
                ) : null}
                <span style={{
                  fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5,
                  color: cc.outcome === 'fulfilled' ? C.limeFg : (C.coralFg ?? '#FF6B47'),
                  border: '1px solid currentColor', borderRadius: 2, padding: '1px 5px', textTransform: 'uppercase' as const,
                }}>
                  {cc.outcome === 'fulfilled' ? 'Fulfilled' : cc.outcome === 'connector_rejected' ? 'Rejected' : cc.outcome === 'backend_unavailable' ? 'Unavailable' : cc.outcome}
                </span>
                {cc.reason !== undefined ? (
                  <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted }}>
                    {cc.reason.replace(/_/g, ' ')}
                  </span>
                ) : null}
                {cc.latency_ms_backend !== undefined ? (
                  <span style={{ fontFamily: monoFont, fontSize: 9, color: C.dim ?? C.muted, marginLeft: 'auto' }}>
                    {cc.latency_ms_backend.toLocaleString('en-US')}ms
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Order — the shared per-line / subtotal / shipping / tax / total
          breakdown. Prefers the UCP checkout breakdown (richest), falls
          back to the settlement evidence. The rail / payment-reference /
          refund / dispute status rows follow below so no evidence is
          lost. Hidden on records that carry neither.

          UCP: the breakdown card already carries its own "ORDER" header,
          so the outer Kicker would read it twice. Non-UCP keeps the
          Kicker so the panel's section rhythm is unchanged. */}
      {(() => {
        const money = readOrderMoney(record);
        if (money === null) return null;
        return (
          <div className="mb-5">
            {isUcp ? null : <Kicker C={C} label="Order" />}
            <OrderMoneyBreakdown C={C} mode="settled" money={money} />
          </div>
        );
      })()}

      {/* ops2026 UCP checkout (2026-10-21) — Payment, Ship to, Placed by.
          Three short rows that let a merchant read the end-to-end story
          of an agent order on Overview: what cleared, where it goes, and
          who placed it. All three are UCP-specific; non-UCP records
          render as before. */}
      {isUcp ? (
        <UcpOrderDetailBlocks C={C} record={record} />
      ) : null}

      {/* Settlement status rows — rail outcome, charge/order id, refund
          + dispute state. Kept separate from the Order section above so
          the money breakdown is readable at a glance and the rail
          evidence does not get buried.

          UCP: the Payment card above folds `SETTLED · Stripe · USD · pi_…`
          into one line, so this block would duplicate that evidence.
          Suppressed on UCP; retained everywhere else. */}
      {isUcp ? null : (() => {
        const s = readSettlement(record);
        if (s === undefined) return null;
        const isRefund = s.kind === 'refund';
        const isFailed = s.outcome === 'failed' || s.outcome === 'aborted';
        const refundedCents = s.refunded_minor ?? 0;
        const totalCents = (isRefund ? s.original_total_minor : s.total_minor) ?? s.total_minor ?? 0;
        const netCents = totalCents - refundedCents;
        const fullRefund = isRefund && netCents === 0;
        const currency = s.currency?.toUpperCase() ?? '';
        const railRef = s.stripe_payment_intent_id ?? s.stripe_refund_id ?? s.shopify_order_id ?? s.stripe_charge_id;

        let outcomeLabel: string;
        let outcomeColor: string;
        if (isRefund) {
          outcomeLabel = fullRefund ? 'Refunded' : 'Partial refund';
          outcomeColor = fullRefund ? (C.coralFg ?? '#FF6B47') : (C.amberFg ?? '#FFB627');
        } else if (isFailed) {
          outcomeLabel = s.outcome === 'aborted' ? 'Aborted' : 'Failed';
          outcomeColor = C.coralFg ?? '#FF6B47';
        } else if (s.outcome === 'refunded') {
          outcomeLabel = 'Refunded';
          outcomeColor = C.coralFg ?? '#FF6B47';
        } else {
          outcomeLabel = 'Settled';
          outcomeColor = C.limeFg;
        }

        return (
          <div className="mb-5">
            <Kicker C={C} label="Settlement status" />
            <div
              className="p-3 space-y-2"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 9,
                    letterSpacing: 1.5,
                    color: outcomeColor,
                    border: '1px solid currentColor',
                    borderRadius: 2,
                    padding: '2px 6px',
                    textTransform: 'uppercase' as const,
                    flexShrink: 0,
                  }}
                >
                  {outcomeLabel}
                </span>
                {s.rail !== undefined ? (
                  <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.2 }}>
                    {s.rail.toUpperCase()}
                  </span>
                ) : null}
                {currency.length > 0 ? (
                  <span style={{ fontFamily: monoFont, fontSize: 9, color: C.dim ?? C.muted, letterSpacing: 1.2 }}>
                    {currency}
                  </span>
                ) : null}
              </div>

              {s.kind === 'refund' && s.original_mandate_id !== undefined ? (
                <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
                  REFUND OF{' '}
                  <span style={{ color: C.text, wordBreak: 'break-all' as const }}>{s.original_mandate_id}</span>
                </div>
              ) : null}

              {s.failure_reason !== undefined ? (
                <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>
                  {s.failure_reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </div>
              ) : null}

              {railRef !== undefined ? (
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    color: C.dim,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {railRef}
                </div>
              ) : null}
            </div>
          </div>
        );
      })()}

      {/* Payment-record linked order (ADR-0157 follow-up 2026-10-25).
          A payment record (settled_succeeded / settled_refunded) carries
          its ordered product, item lines and totals via the server-
          resolved `linked_order` block (fetched tenant-scoped on the
          single-record route). Keeps the merchant from needing a second
          dashboard call to find out what was bought. Protocol row
          renders alongside so the record names how the agent paid. */}
      {(record.decision === 'settled_succeeded' || record.decision === 'settled_refunded')
        && (record as MandateRecord).linked_order !== undefined ? (() => {
        const lo = (record as MandateRecord).linked_order;
        if (lo === undefined) return null;
        // Project the linked order's money as an `OrderMoney` so we can
        // reuse the shared breakdown component. Priority: UCP checkout
        // breakdown > settlement line_items + totals > nothing.
        let linkedMoney: OrderMoney | null = null;
        if (lo.order_breakdown !== undefined) {
          linkedMoney = fromUcpBreakdown(lo.order_breakdown);
        } else if (lo.settlement !== undefined) {
          const s = lo.settlement;
          const has_lines = Array.isArray(s.line_items) && s.line_items.length > 0;
          const has_total = typeof s.total_minor === 'number';
          if (has_lines || has_total) linkedMoney = fromSettlement(s);
        }
        const linkedProtocol = lo.derived_protocol;
        const linkedMandateId = lo.mandate_id;
        return (
          <div className="mb-5" data-testid="linked-order-block">
            <Kicker C={C} label="Order placed" />
            <div
              className="p-3 space-y-3"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              {linkedMoney !== null ? (
                <OrderMoneyBreakdown C={C} mode="settled" money={linkedMoney} />
              ) : null}
              <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
                PROTOCOL
                <span style={{ color: C.text, marginLeft: 8 }}>
                  {protocolLabel(linkedProtocol)}
                </span>
              </div>
              {linkedMandateId !== undefined ? (
                <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
                  ORDER MANDATE
                  <span style={{ color: C.text, marginLeft: 8, wordBreak: 'break-all' }}>
                    {linkedMandateId}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        );
      })() : null}

      {/* Escalation status.

          UCP: the header already carries the "ESCALATED → APPROVED"
          pill, and the Payment card's opening sentence reads "Held by
          your rules → Approved → Charged …". A separate escalation box
          would be a third copy of the same beat. Suppressed on UCP. */}
      {isUcp ? null : (() => {
        const d = record.decision;
        if (d !== 'escalated_approved' && d !== 'escalated_rejected') return null;
        const isApproved = d === 'escalated_approved';
        const color = isApproved ? C.limeFg : (C.coralFg ?? '#FF6B47');
        const label = isApproved ? 'Escalation approved' : 'Escalation rejected';
        return (
          <div className="mb-5">
            <Kicker C={C} label="Escalation" />
            <div className="flex items-center gap-2 p-2" style={{ border: `1px solid ${color}`, borderRadius: 2 }}>
              <span style={{ fontFamily: monoFont, fontSize: 9, color, letterSpacing: 1.5, textTransform: 'uppercase' as const }}>
                {label}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Rules summary — compact pass/fail list.
          Each chip is `whiteSpace: 'nowrap'` so a rule id never breaks
          mid-slug (`Max_1_test` kept together); the row wraps between
          chips with a consistent 8/4px gap. */}
      {record.rules_evaluated.length > 0 ? (
        <div className="mb-5">
          <Kicker C={C} label="Rules" />
          <div
            data-testid="rules-row"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 10,
              rowGap: 6,
            }}
          >
            {record.rules_evaluated.map((rule, i) => {
              const r = rule as { rule_id?: string; passed?: boolean; outcome?: string; action_taken?: string; reason?: string };
              // ✓ passed · ✗ broke the rule (it decided the outcome) · – never
              // ran because an earlier rule already decided.
              const state = ruleResultState(r);
              const mark = state === 'passed' || state === 'exempted' ? '✓' : state === 'failed' ? '✗' : '–';
              const color = state === 'passed' ? C.limeFg
                : state === 'exempted' ? C.amberFg
                : state === 'failed' ? (C.coralFg ?? '#FF6B47')
                : C.dim;
              return (
                <Tooltip key={i} content={RULE_STATE_TOOLTIP[state]}>
                  <span
                    data-rule-state={state}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      cursor: 'help',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ color }} aria-label={RULE_STATE_LABEL[state].toLowerCase()}>{mark}</span>
                    <span style={{ color: state === 'not_checked' ? C.dim : C.muted }}>{r.rule_id ?? `rule ${i + 1}`}</span>
                  </span>
                </Tooltip>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Details — UCP's trimmed facts (Policy · Mandate ID · Time)
          slotted here AFTER Rules so money/decision reads first and
          reference data reads last (founder polish 2026-10-21). The
          same three facts live inside the Key-facts block for non-UCP
          records above; non-UCP skips this slot. */}
      {isUcp ? (
        <div className="mb-5" data-testid="ucp-details-block">
          <Kicker C={C} label="Details" />
          <dl className="space-y-3">
            {/* ADR-0253 §A + founder bug 2026-10-25 — show the Protocol
                row on every order, including UCP. The row reads "UCP"
                on a UCP checkout; "Not recorded" when nothing was
                captured (never "Unknown"). */}
            <KeyFactRow
              C={C}
              label="Protocol"
              tooltip={protocolTooltip(recordDerivedProtocol(record))}
            >
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  background: C.surface2,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  padding: '2px 6px',
                }}
              >
                {protocolLabel(recordDerivedProtocol(record))}
              </span>
            </KeyFactRow>
            {policy_label !== null ? (
              <KeyFactRow
                C={C}
                label="Policy"
                tooltip={`The rules on your Rules page that checked this request. Version counts how many times they have been published. Exact id in the signed record: ${record.policy_version ?? ''}`}
              >
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    padding: '2px 6px',
                  }}
                >
                  {policy_label}
                </span>
              </KeyFactRow>
            ) : null}
            {record.mandate_id !== undefined ? (
              <KeyFactRow C={C} label="Mandate ID" tooltip="The signed mandate reference for this transaction. Click to copy — use this ID when contacting support.">
                <Tooltip content="Click to copy mandate ID">
                  <button
                    type="button"
                    onClick={() => { try { void navigator.clipboard.writeText(record.mandate_id!); } catch { /* ignore */ } }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.muted,
                      cursor: 'pointer',
                      wordBreak: 'break-all',
                      textAlign: 'left',
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                    }}
                  >
                    {record.mandate_id}
                  </button>
                </Tooltip>
              </KeyFactRow>
            ) : null}
            <KeyFactRow
              C={C}
              label="Time"
              tooltip={groupLastSeen !== undefined ? 'The newest visit in this row.' : 'The timestamp of this specific audit record.'}
            >
              {formatIsoDate(groupLastSeen ?? record.evaluated_at, timeZone)}
            </KeyFactRow>
          </dl>
        </div>
      ) : null}

      {/* Agent activity on this site — top pages + search terms from history */}
      {(topPages.length > 0 || topSearches.length > 0) ? (
        <div className="mb-5">
          <Kicker C={C} label="Activity on your site" />
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 2, padding: '8px 12px', background: C.surface2 }}>
            {topPages.length > 0 ? (
              <div className={topSearches.length > 0 ? 'mb-3' : ''}>
                <div style={{ fontFamily: monoFont, fontSize: 8, color: C.muted, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}>
                  Top pages
                </div>
                {topPages.map(({ path, count }) => (
                  <div key={path} className="flex items-center justify-between" style={{ marginBottom: 3 }}>
                    <span style={{ fontFamily: monoFont, fontSize: 10, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                      {path}
                    </span>
                    <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, flexShrink: 0, marginLeft: 8 }}>
                      {count.toLocaleString('en-US')}×
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            {topSearches.length > 0 ? (
              <div>
                <div style={{ fontFamily: monoFont, fontSize: 8, color: C.muted, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}>
                  Search terms
                </div>
                {topSearches.map(({ term, count }) => (
                  <div key={term} className="flex items-center justify-between" style={{ marginBottom: 3 }}>
                    <span style={{ fontFamily: sansFont, fontSize: 11, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                      &ldquo;{term}&rdquo;
                    </span>
                    <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, flexShrink: 0, marginLeft: 8 }}>
                      {count.toLocaleString('en-US')}×
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Suspicious v2 (founder 2026-10-18) — Report to Network moved
          into the top-right DetailActionRow's ⋯ menu. The bottom-of-
          Overview button is gone; the handler lives on the main
          component now so the ⋯ menu can call it. */}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity trace row — one key/value row inside the expanded trace
// ---------------------------------------------------------------------------

/** Label column shared by every trace row, so values line up in one column. */
const TRACE_LABEL_WIDTH = 88;
const TRACE_ROW_GAP = 8;

function TraceLabel({ C, label, tooltip }: { C: ReturnType<typeof useC>; label: string; tooltip?: string }): JSX.Element {
  const text = (
    <span
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        lineHeight: '16px',
        ...(tooltip !== undefined ? { cursor: 'help', borderBottom: `1px dotted ${C.border}` } : {}),
      }}
    >
      {label}
    </span>
  );
  return (
    <span style={{ width: TRACE_LABEL_WIDTH, flexShrink: 0 }}>
      {tooltip !== undefined ? <Tooltip content={tooltip}>{text}</Tooltip> : text}
    </span>
  );
}

function TraceRow({
  C,
  label,
  children,
  tone,
  tooltip,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  children: React.ReactNode;
  tone?: 'normal' | 'muted' | 'ok' | 'warn' | 'bad';
  /** Plain-language meaning of the row, shown on the label. */
  tooltip?: string;
}): JSX.Element {
  const valueColor =
    tone === 'ok' ? C.limeFg ?? C.lime
    : tone === 'warn' ? C.amberFg ?? '#FFB627'
    : tone === 'bad' ? C.coralFg ?? '#FF6B47'
    : tone === 'muted' ? C.muted
    : C.text;
  return (
    <div className="flex items-start gap-3" style={{ marginBottom: TRACE_ROW_GAP }}>
      <TraceLabel C={C} label={label} {...(tooltip !== undefined ? { tooltip } : {})} />
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: valueColor,
          lineHeight: '16px',
          wordBreak: 'break-all',
          minWidth: 0,
        }}
      >
        {children}
      </span>
    </div>
  );
}

/**
 * Founder 2026-10-18 (ADR-0247 follow-up) — per-event identity trust
 * row on the collapsible event's Identity block. Reads the signed
 * `identity_verification` field on the record; no extra fetch. Mint
 * ShieldCheck + "Verified · <label>" for signature / published IP /
 * rDNS; muted ShieldAlert + "Unverified · name only" otherwise.
 * Mismatch keeps a coral tone + the reason.
 */
const TRUST_TOOLTIP =
  'How sure Sill is about who sent this. Verified means the sender proved its identity: a valid request signature, '
  + 'an address from the operator\'s published list, or a matching reverse DNS name. '
  + 'Unverified · name only means the sender told us its name but did not prove it, so treat the name as a claim.';

/**
 * ops2026 UCP checkout (2026-10-21) — the trust statement for a UCP
 * record. Every UCP request is RFC 9421-signed and verified against the
 * platform's published profile BEFORE Sill accepts it (the relay rejects
 * unsigned / invalid requests with `protocol_unsupported` / `sig_invalid`
 * — see `apps/edge/pipeline.ts` and the UCP verifier primitives at
 * `packages/ucp`). So a UCP audit record exists only when the signature
 * cleared; the UI reads trust directly from the signed `inbound_protocol
 * === 'ucp'` field rather than per-record identity-check cache state.
 *
 * Used by both the header (compact chip) and the event-trace Trust row
 * (full row with the Trust label), so a merchant reads the same words
 * in both places.
 */
export const UCP_TRUST_TOOLTIP =
  'Every UCP request is signed by the agent platform and checked against '
  + 'the keys it publishes before Sill accepts it.';

/** ADR-0215 §11 — the order came in on the merchant-issued access key. */
export const UCP_KEY_TRUST_TOOLTIP =
  'The agent platform signed in with the access key you created for it in '
  + 'Settings. Sill checked the key before accepting the order; these '
  + 'requests were not signed.';

/** Where the record does not say which (list rows): true for both. */
export const UCP_CHECKED_TOOLTIP =
  'Sill checked the agent platform before accepting this order — its '
  + 'signature, or the access key you created for it.';

function ucpTrustCopy(auth: UcpPlatformAuth | null | undefined): { label: string; tooltip: string } {
  if (auth === 'api_key') return { label: 'Verified with access key', tooltip: UCP_KEY_TRUST_TOOLTIP };
  if (auth === 'signature') return { label: 'Signed and verified', tooltip: UCP_TRUST_TOOLTIP };
  return { label: 'Platform verified', tooltip: UCP_CHECKED_TOOLTIP };
}

/**
 * UCP trust glyph: the same mint shield-check + sans text pattern the rest
 * of the dashboard uses for verified senders (see `VisitIdentityMark` in
 * `VisitIdentityMarks.tsx`, which renders "mint ShieldCheck · Confirmed"
 * after a sender's name).
 *
 *   - `surface="row"` (default): shield only — placed after the name in a
 *     list row, exactly like `VisitIdentityMark` without a label.
 *   - `surface="header"`: shield + "Signed and verified" sans label, placed
 *     after the name in the detail header, like `VisitIdentityMark` with
 *     `withLabel`.
 *
 * No pill border. Mint. One Tooltip wraps both so hover/focus/tap reveal
 * the same explanation on every surface.
 */
export function UcpPlatformTrustMark({
  C,
  surface = 'row',
  size,
  auth,
}: {
  readonly C: ReturnType<typeof useC>;
  readonly surface?: 'row' | 'header';
  readonly size?: number;
  /** From `ucpPlatformAuth(record)`; `null`/absent → wording true for both. */
  readonly auth?: UcpPlatformAuth | null;
}): JSX.Element {
  const iconSize = size ?? (surface === 'header' ? 12 : 12);
  const copy = ucpTrustCopy(auth);
  return (
    <Tooltip content={copy.tooltip} side="bottom" aria-label={`${copy.label}: ${copy.tooltip}`}>
      <span
        data-testid="ucp-trust-mark"
        data-surface={surface}
        className="inline-flex items-center gap-1"
        style={{
          color: C.mintFg,
          fontFamily: sansFont,
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        <ShieldCheck size={iconSize} color={C.mintFg} aria-hidden="true" style={{ flexShrink: 0 }} />
        {surface === 'header' ? <span>{copy.label}</span> : null}
      </span>
    </Tooltip>
  );
}

export function UcpPlatformTrustRow({
  C,
  auth,
}: {
  readonly C: ReturnType<typeof useC>;
  readonly auth?: UcpPlatformAuth | null;
}): JSX.Element {
  const copy = ucpTrustCopy(auth);
  return (
    <div
      className="flex items-start gap-3"
      data-testid="event-trust-row"
      data-verification={auth === 'api_key' ? 'ucp_api_key' : auth === 'signature' ? 'ucp_signed' : 'ucp_checked'}
      style={{ marginBottom: TRACE_ROW_GAP }}
    >
      <TraceLabel C={C} label="Trust" tooltip={copy.tooltip} />
      <Tooltip content={copy.tooltip} side="bottom">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 16 }}>
          <ShieldCheck size={12} color={C.mintFg} style={{ flexShrink: 0 }} aria-hidden="true" />
          <span style={{ fontFamily: monoFont, fontSize: 11, lineHeight: '16px', color: C.text }}>
            {copy.label}
          </span>
        </span>
      </Tooltip>
    </div>
  );
}

export function EventTrustRow({
  C,
  record: r,
}: {
  readonly C: ReturnType<typeof useC>;
  readonly record: AuditRecord;
}): JSX.Element {
  if (isUcpOrder(r)) return <UcpPlatformTrustRow C={C} auth={ucpPlatformAuth(r)} />;
  const kind = r.identity_verification;
  const isVerified = kind === 'signature' || kind === 'published_ip' || kind === 'rdns';
  const isMismatch = kind === 'failed';
  const label = kind === 'signature' ? 'Verified · signed'
    : kind === 'published_ip' ? "Verified · published addresses"
    : kind === 'rdns' ? 'Verified · reverse DNS'
    : isMismatch ? 'Unverified · failed check'
    : 'Unverified · name only';
  const colour = isVerified ? C.mintFg : isMismatch ? C.coralFg : C.muted;
  const Icon = isVerified ? ShieldCheck : ShieldAlert;
  return (
    <div
      className="flex items-start gap-3"
      data-testid="event-trust-row"
      data-verification={kind ?? 'unknown'}
      style={{ marginBottom: TRACE_ROW_GAP }}
    >
      <TraceLabel C={C} label="Trust" tooltip={TRUST_TOOLTIP} />
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 16 }}>
        <Icon size={12} color={colour} style={{ flexShrink: 0 }} />
        <span style={{ fontFamily: monoFont, fontSize: 11, lineHeight: '16px', color: isVerified ? C.text : C.muted }}>
          {label}
        </span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityEventRow — one collapsible event in the agent history list
// ---------------------------------------------------------------------------

function ActivityEventRow({
  C,
  record: r,
  payload,
  buyerReveal,
  isCurrent,
}: {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
  payload?: AuditRecordPayload;
  buyerReveal: 'auto' | 'on_click';
  isCurrent: boolean;
}): JSX.Element {
  const [expanded, setExpanded] = useState(isCurrent);
  const timeZone = useDisplayTimeZone();
  const is_discovery = r.decision === 'observed';
  const settlement = readSettlement(r);
  const handoff = readHandoff(r);
  const original = readOriginalOrder(r);
  const has_rules_trace = r.rules_evaluated.length > 0;
  const rules_carry_advisory = r.rules_evaluated.some(
    (rule) => (rule as { surface?: string }).surface === 'tool_call_advisory',
  );
  const advisory_mode =
    rules_carry_advisory || (is_discovery && r.connector_call !== undefined && has_rules_trace);
  const skill_id = r.skill_id ?? r.connector_call?.skill_id;
  const action_label = skill_id !== undefined ? friendlyActionLabel(skill_id) : undefined;
  const outcome = outcomePresentation(r);
  const agent_id = r.identification_match?.agent_id ?? r.rejected_agent_id;
  // Mismatch event: the claimed name in quotes, never the brand display name.
  const event_id = identityState(r);
  const agent_name = event_id.state === 'mismatch'
    ? quotedName(event_id.name)
    : agent_id !== undefined ? displayAgentName(agent_id) : null;
  const cls = r.identification_match?.visitor_class;

  return (
    <div
      style={{
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        marginBottom: 0,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => { setExpanded((v) => !v); }}
        aria-expanded={expanded}
        className="w-full p-3"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          // Narrow panels (390 px) used to overflow and clip the right-hand
          // time + record id. The row now wraps cleanly: the icon + status
          // mark stay on the first line, the page path and time wrap under
          // when there isn't room. flexWrap + rowGap keeps the breakage
          // consistent across themes.
          flexWrap: 'wrap',
          columnGap: 12,
          rowGap: 4,
        }}
      >
        {expanded
          ? <ChevronDown size={12} color={C.muted} style={{ flexShrink: 0 }} />
          : <ChevronRight size={12} color={C.muted} style={{ flexShrink: 0 }} />}
        {/* Founder bug 2026-10-21 — the current event's row USED to echo
            the header's decision pill ("ESCALATED · APPROVED mandate THIS
            EVENT Oct…"); that line now reads once above. We only render
            the per-event outcome pill on the OTHER rows (session history,
            where the pill is the only signal of a per-event decision). */}
        {is_discovery && action_label === undefined
          ? (
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                border: `1px solid ${C.dim}`,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
          )
          : isCurrent
            ? null
            : <Pill tone={outcome.tone}>{outcome.label}</Pill>
        }
        {isCurrent ? (
          <span style={{ fontFamily: monoFont, fontSize: 9, color: C.limeFg, letterSpacing: 1.5, flexShrink: 0 }}>
            THIS EVENT
          </span>
        ) : null}
        <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text, flexShrink: 0 }}>
          {action_label ?? (is_discovery ? 'visit' : 'mandate')}
        </span>
        {r.page_path !== undefined && r.page_path.length > 0 ? (
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              flex: '1 1 120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
            title={r.page_path} /* title-ok: full path of the ellipsised page */
          >
            {r.page_path}
          </span>
        ) : <span style={{ flex: 1 }} />}
        <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, flexShrink: 0 }}>
          {formatIsoDate(r.evaluated_at, timeZone)}
        </span>
      </button>

      {expanded ? (
        <div className="p-3" style={{ borderTop: `1px solid ${C.border}` }}>
          {/* IDENTITY block */}
          <div className="mb-4">
            <Kicker C={C} label="Identity" />
            {/* Founder 2026-10-18 (ADR-0247 follow-up): per-event
                trust mark. "Verified · <label>" when this specific
                event's identity_verification is proven; "Unverified ·
                name only" otherwise. Reads the signed field; no
                extra fetch. */}
            <EventTrustRow C={C} record={r} />
            {agent_name !== null ? (
              <TraceRow C={C} label="Agent">{agent_name}</TraceRow>
            ) : null}
            {r.user_agent !== undefined && r.user_agent.length > 0 ? (
              <div className="flex items-start gap-3" style={{ marginBottom: TRACE_ROW_GAP }}>
                <TraceLabel C={C} label="User-agent" tooltip="The name string the sender's software sent with the request. Anyone can set it." />
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.muted,
                    lineHeight: '16px',
                    minWidth: 0,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-all',
                  }}
                  title={r.user_agent} /* title-ok: full value of the 2-line-clamped user agent */
                >
                  {r.user_agent}
                </span>
              </div>
            ) : null}
            {/* Visits panel Step A (labels-v2): the per-event "Signed
                request" and Cloudflare "Identity check" rows are removed.
                They stated identity without the server verdict (and read
                cf_verified_bot). Signature detail for this record lives in
                Raw data → Identity. */}
            {cls !== undefined && cls !== 'unknown_agent' ? (
              <TraceRow C={C} label="Class" tone="muted">
                {({
                  matched_agent: 'Known agent',
                  unknown_human: 'Likely human',
                  matched_human: 'Known human',
                  human_likely: 'Likely human',
                  bot: 'Bot',
                } as Record<string, string>)[cls] ?? cls.replace(/_/g, ' ')}
              </TraceRow>
            ) : null}
            {/* ADR-0253 §A — Protocol row ALWAYS renders in the Raw-data
                trace too. Derived value only; "Unknown" when nothing
                matched. */}
            {(() => {
              const dp = recordDerivedProtocol(r);
              return (
                <TraceRow C={C} label="Protocol" tone="muted" tooltip={protocolTooltip(dp)}>
                  {protocolLabel(dp)}
                </TraceRow>
              );
            })()}
            {r.a2a_task_id !== undefined && r.a2a_task_id.length > 0 ? (
              <TraceRow
                C={C}
                label="A2A task"
                tone="muted"
                tooltip="The A2A Task id this message was dispatched from. Matches the task_id in the response envelope the agent received."
              >
                {r.a2a_task_id}
              </TraceRow>
            ) : null}
            {seenByLabel(r) !== null ? (
              <TraceRow C={C} label="Seen by" tone="muted" tooltip={SEEN_BY_TOOLTIP}>
                {seenByLabel(r)}
              </TraceRow>
            ) : null}
          </div>

          {/* PAGE block — full path (header shows truncated version) */}
          {r.page_path !== undefined && r.page_path.length > 0 ? (
            <div className="mb-4">
              <Kicker C={C} label="Page" />
              <TraceRow C={C} label="Path" tone="muted">{r.page_path}</TraceRow>
              {(() => {
                if (r.page_query === undefined || r.page_query.length === 0) return null;
                const SEARCH_KEYS = ['q', 's', 'search', 'query', 'keyword', '_search'];
                let term: string | null = null;
                try {
                  const params = new URLSearchParams(
                    r.page_query.startsWith('?') ? r.page_query.slice(1) : r.page_query,
                  );
                  for (const k of SEARCH_KEYS) {
                    const v = params.get(k);
                    if (v !== null && v.trim().length > 0) { term = v.trim(); break; }
                  }
                } catch { /* malformed */ }
                if (term === null) return null;
                return <TraceRow C={C} label="Search">{term}</TraceRow>;
              })()}
            </div>
          ) : null}

          {/* PAGES block — every load behind this record (ADR-0210 Slice 9).
              The signed record is one per (site, UA, hour) and names only
              its first path; this is the rest of the visit, in order. */}
          {r.page_hits !== undefined && r.page_hits.length > 0 ? (
            <div className="mb-4" data-testid="page-hits">
              <Kicker C={C} label={`Pages loaded · ${r.page_hits.length.toLocaleString('en-US')} in this hour`} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {r.page_hits.map((h, i) => {
                  const prev = r.page_hits![i - 1];
                  const new_session = prev !== undefined && prev.session_key !== h.session_key;
                  const t = new Date(h.hit_at);
                  const hhmm = Number.isNaN(t.getTime()) ? '' : t.toISOString().slice(11, 16);
                  return (
                    <div key={`${h.hit_at}-${i}`} style={{ display: 'grid', gridTemplateColumns: '44px 1fr auto', gap: 8, alignItems: 'baseline', borderTop: new_session ? `1px dashed ${C.border}` : 'none', paddingTop: new_session ? 4 : 0 }}>
                      <span style={{ fontFamily: monoFont, fontSize: 10, color: C.dim }}>{hhmm}</span>
                      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {h.page_path}
                        {h.search_term !== null ? <span style={{ color: C.muted }}> · “{h.search_term}”</span> : null}
                      </span>
                      <span style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1, color: h.page_kind === 'checkout' || h.page_kind === 'cart' ? C.limeFg : C.dim, textTransform: 'uppercase' }}>{h.page_kind}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontFamily: sansFont, fontSize: 10, color: C.dim, marginTop: 6 }}>
                Unsigned page loads behind this signed record. A dashed line marks a new session (30-minute gap).
              </div>
            </div>
          ) : null}

          {r.connector_call?.latency_ms_backend !== undefined ? (
            <div className="mb-4">
              <Kicker C={C} label="Connector" />
              <TraceRow C={C} label="Latency" tone="muted">
                {r.connector_call.latency_ms_backend.toLocaleString()} ms
              </TraceRow>
            </div>
          ) : null}

          {settlement?.kind !== 'refund' && readOrderBreakdown(r) !== undefined ? (
            <div className="mb-4">
              <OrderMoneyBreakdown
                C={C}
                mode="settled"
                money={fromUcpBreakdown(readOrderBreakdown(r)!)}
              />
            </div>
          ) : r.intent_summary !== undefined && settlement?.kind !== 'refund' ? (
            <div className="mb-4">
              <Kicker C={C} label="Line items" />
              <RequestedLineItems
                C={C}
                intent={r.intent_summary}
                settlementLines={settlement?.line_items}
              />
            </div>
          ) : null}

          {/* UCP event: ship-to summary inline with the breakdown, same
              shape the Overview panel renders. Keeps a merchant reading
              one record end-to-end without flipping tabs. On the
              Activity tab the Buyer detail panel is actually below, so
              the note points there (Overview uses "under Activity →
              Buyer detail" to send the reader to the right tab). */}
          {(() => {
            const ob = readOrderBreakdown(r);
            if (ob?.ship_to === undefined || ob.ship_to === null) return null;
            return (
              <div className="mb-4" data-testid="event-ucp-ship-to">
                <Kicker C={C} label="Ship to" />
                <div
                  className="flex items-center gap-2 p-3"
                  style={{
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.text,
                    overflowWrap: 'anywhere',
                  }}
                >
                  <CountryFlag code={ob.ship_to.country} size={16} />
                  <span>
                    {ob.ship_to.region !== null && ob.ship_to.region.length > 0 ? `${ob.ship_to.region}, ` : ''}
                    {countryName(ob.ship_to.country) ?? ob.ship_to.country}
                  </span>
                </div>
                {!is_discovery ? (
                  <div
                    data-testid="event-ucp-ship-to-note"
                    style={{
                      fontFamily: sansFont,
                      fontSize: 11,
                      color: C.muted,
                      lineHeight: 1.5,
                      marginTop: 6,
                    }}
                  >
                    Full address is in Buyer detail below.
                  </div>
                ) : null}
              </div>
            );
          })()}

          {/* Payment / settlement.
              UCP events render the compact Payment card (one status
              sentence + one meta line: SETTLED · Stripe · USD · pi_…),
              matching Overview and dropping the separate SettlementPanel
              so the trace doesn't carry two copies of the same evidence.
              Refunds still route to RefundBlock (merchant workflow). */}
          {settlement?.kind === 'refund' ? (
            <div className="mb-4">
              <RefundBlock
                C={C}
                settlement={settlement}
                intent={r.intent_summary}
                decision={r.decision}
              />
            </div>
          ) : isUcpOrder(r) && settlement !== undefined ? (
            <UcpEventPaymentBlock C={C} record={r} />
          ) : settlement !== undefined ? (
            <SettlementPanel
              C={C}
              settlement={settlement}
              requestedDiscountCode={r.intent_summary?.discount?.code}
            />
          ) : null}

          <HandoffPanel C={C} handoff={handoff} />

          {settlement?.kind === 'refund' ? (
            <div className="mb-4">
              <OriginalOrderPanel C={C} original={original ?? null} />
            </div>
          ) : null}

          {!is_discovery && settlement?.kind !== 'refund' ? (
            <div className="mb-4">
              <Kicker C={C} label="Buyer" />
              <BuyerDetailPanel record_id={r.record_id} reveal={buyerReveal} />
            </div>
          ) : null}

          {advisory_mode ? (
            <AdvisoryVerdictPill C={C} rules={r.rules_evaluated} />
          ) : null}
          {has_rules_trace ? (
            <RulesTrace C={C} record={r} advisory={advisory_mode} />
          ) : null}

          {isCurrent && payload !== undefined ? (
            <div className="mb-4">
              <Kicker C={C} label="The exchange" />
              <PayloadPanel
                C={C}
                payload={payload}
                request_hash={r.request_hash ?? ''}
                response_hash={r.response_hash ?? ''}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Session grouping — 30-min idle window separates sessions.
// Records come in newest-first; sessions are returned newest-first.
// ---------------------------------------------------------------------------

const SESSION_GAP_MS = 30 * 60 * 1000;

interface Session {
  /** Oldest-first within the session */
  records: AuditRecord[];
  /** 1-indexed, 1 = oldest session */
  index: number;
}

function groupIntoSessions(records: AuditRecord[]): Session[] {
  if (records.length === 0) return [];
  // Sort oldest-first to detect gaps in chronological order
  const sorted = [...records].sort(
    (a, b) => new Date(a.evaluated_at).getTime() - new Date(b.evaluated_at).getTime(),
  );
  const groups: AuditRecord[][] = [[sorted[0]!]];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    const curr = sorted[i]!;
    const gap = new Date(curr.evaluated_at).getTime() - new Date(prev.evaluated_at).getTime();
    if (gap > SESSION_GAP_MS) {
      groups.push([curr]);
    } else {
      groups[groups.length - 1]!.push(curr);
    }
  }
  // Return newest session first; assign 1-based index from oldest
  const sessions: Session[] = groups.map((recs, i) => ({
    records: recs,
    index: i + 1,
  }));
  return sessions.reverse();
}

function formatSessionDuration(records: AuditRecord[]): string | null {
  if (records.length < 2) return null;
  const times = records.map((r) => new Date(r.evaluated_at).getTime());
  const ms = Math.max(...times) - Math.min(...times);
  if (ms < 60_000) return `${Math.round(ms / 1000)} seconds`;
  if (ms < 3_600_000) {
    const mins = Math.round(ms / 60_000);
    return `${mins} minute${mins !== 1 ? 's' : ''}`;
  }
  const hrs = Math.round(ms / 3_600_000);
  return `${hrs} hour${hrs !== 1 ? 's' : ''}`;
}

// Severity order: coral > amber > mint > lime > sky/anything else
const TONE_RANK: Record<string, number> = {
  coral: 4, amber: 3, mint: 2, lime: 1, sky: 0,
};

function sessionOutcome(records: AuditRecord[]): ReturnType<typeof outcomePresentation> {
  let best = outcomePresentation(records[0]!);
  for (const r of records.slice(1)) {
    const o = outcomePresentation(r);
    if ((TONE_RANK[o.tone] ?? 0) > (TONE_RANK[best.tone] ?? 0)) best = o;
  }
  return best;
}

// ---------------------------------------------------------------------------
// SessionBlock — collapsible session container with header + event timeline
// ---------------------------------------------------------------------------

function SessionBlock({
  C,
  session,
  totalSessions,
  currentRecordId,
  record,
  payload,
  buyerReveal,
}: {
  C: ReturnType<typeof useC>;
  session: Session;
  totalSessions: number;
  currentRecordId: string;
  record: AuditRecord;
  payload?: AuditRecordPayload;
  buyerReveal: 'auto' | 'on_click';
}): JSX.Element {
  const hasCurrent = session.records.some((r) => r.record_id === currentRecordId);
  const [expanded, setExpanded] = useState(hasCurrent);
  const outcome = sessionOutcome(session.records);
  const duration = formatSessionDuration(session.records);
  const count = session.records.length;
  const dotColor =
    outcome.tone === 'lime' || outcome.tone === 'mint' ? C.limeFg
    : outcome.tone === 'coral' ? C.coralFg ?? '#FF6B47'
    : outcome.tone === 'amber' ? C.amberFg ?? '#FFB627'
    : C.dim;

  return (
    <div style={{ marginBottom: 8 }}>
      {/* Session header */}
      <button
        type="button"
        onClick={() => { setExpanded((v) => !v); }}
        className="w-full"
        style={{
          background: 'transparent',
          border: 'none',
          borderRadius: 2,
          padding: '4px 6px',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '2px 6px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {expanded
            ? <ChevronDown size={10} color={C.dim} />
            : <ChevronRight size={10} color={C.dim} />}
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              letterSpacing: 1.5,
              color: C.muted,
              textTransform: 'uppercase',
            }}
          >
            Session {totalSessions - session.index + 1}
          </span>
        </span>
        <span style={{ fontFamily: monoFont, fontSize: 9, color: C.dim, flexShrink: 0 }}>
          {count.toLocaleString('en-US')} {count === 1 ? 'request' : 'requests'}
          {duration !== null ? ` · ${duration}` : ''}
        </span>
        <span style={{ flexShrink: 0 }}>
          <Pill tone={outcome.tone}>{outcome.label}</Pill>
        </span>
        {hasCurrent ? (
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 8,
              color: C.limeFg,
              letterSpacing: 1.5,
              flexShrink: 0,
            }}
          >
            ← THIS SESSION
          </span>
        ) : null}
      </button>

      {/* Events */}
      {expanded ? (
        <div style={{ position: 'relative', paddingLeft: 0 }}>
          {/* Vertical rail */}
          <div
            style={{
              position: 'absolute',
              left: 15,
              top: 6,
              bottom: 6,
              width: 1,
              background: C.border,
            }}
          />
          {/* Events newest-first (session.records is oldest-first, reverse for display) */}
          {[...session.records].reverse().map((r, idx, arr) => {
            const isCurrent = r.record_id === currentRecordId;
            const eventOutcome = outcomePresentation(r);
            const edotColor =
              eventOutcome.tone === 'lime' || eventOutcome.tone === 'mint' ? C.limeFg
              : eventOutcome.tone === 'coral' ? C.coralFg ?? '#FF6B47'
              : eventOutcome.tone === 'amber' ? C.amberFg ?? '#FFB627'
              : C.dim;
            return (
              <div
                key={r.record_id}
                style={{ display: 'flex', gap: 0, position: 'relative' }}
              >
                <div
                  style={{
                    width: 31,
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 10,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: isCurrent ? 10 : 7,
                      height: isCurrent ? 10 : 7,
                      borderRadius: '50%',
                      background: isCurrent ? edotColor : 'transparent',
                      border: `1.5px solid ${isCurrent ? edotColor : C.dim}`,
                      flexShrink: 0,
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingBottom: idx < arr.length - 1 ? 4 : 0 }}>
                  <ActivityEventRow
                    C={C}
                    record={r}
                    buyerReveal={buyerReveal}
                    isCurrent={isCurrent}
                    {...(isCurrent && payload !== undefined ? { payload } : {})}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity tab — always shows identity block, then event-specific content
// ---------------------------------------------------------------------------

function ActivityTab({
  C,
  record,
  payload,
  buyerReveal,
  agentHistory,
  agentHistoryLoading,
}: {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
  payload?: AuditRecordPayload;
  buyerReveal: 'auto' | 'on_click';
  agentHistory?: AuditRecord[];
  agentHistoryLoading?: boolean;
}): JSX.Element {
  // Loading state — show skeleton rows instead of blank whitespace on initial load.
  if (agentHistoryLoading === true && agentHistory === undefined) {
    return (
      <div>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="mb-3 p-3"
            style={{
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              background: C.surface2,
              opacity: 1 - i * 0.25,
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ width: 48, height: 8, background: C.border, borderRadius: 1 }} />
              <div style={{ width: 80, height: 8, background: C.border, borderRadius: 1 }} />
              <div style={{ flex: 1 }} />
              <div style={{ width: 60, height: 8, background: C.border, borderRadius: 1 }} />
            </div>
            <div style={{ marginTop: 8, width: '60%', height: 8, background: C.border, borderRadius: 1 }} />
          </div>
        ))}
        <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5, textAlign: 'center', marginTop: 8 }}>
          LOADING ACTIVITY…
        </div>
      </div>
    );
  }

  // When agent history is available, delegate entirely to the multi-event list.
  if (agentHistory !== undefined && agentHistory.length > 0) {
    const sessions = groupIntoSessions(agentHistory);
    const sessionLabel = sessions.length === 1
      ? `1 SESSION · ${agentHistory.length.toLocaleString('en-US')} EVENT${agentHistory.length !== 1 ? 'S' : ''}`
      : `${sessions.length.toLocaleString('en-US')} SESSIONS · ${agentHistory.length.toLocaleString('en-US')} EVENT${agentHistory.length !== 1 ? 'S' : ''}`;
    return (
      <div>
        {/* 24h sparkline — activity density over the last day */}
        <ActivitySparkline C={C} records={agentHistory} />

        {agentHistoryLoading === true ? (
          <div
            style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5, marginBottom: 8 }}
          >
            LOADING HISTORY…
          </div>
        ) : (
          <div
            style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5, marginBottom: 10 }}
          >
            {sessionLabel}
          </div>
        )}
        {sessions.map((session) => (
          <SessionBlock
            key={session.index}
            C={C}
            session={session}
            totalSessions={sessions.length}
            currentRecordId={record.record_id}
            record={record}
            buyerReveal={buyerReveal}
            {...(payload !== undefined ? { payload } : {})}
          />
        ))}
      </div>
    );
  }

  // Fallback: single-event view when no agent_id to query by, or loading.
  return (
    <div>
      {/* Event meta strip — reads "THIS EVENT · 5m ago · <record id>".
          The id is long on purpose (ULIDs are 26 chars) so the strip must
          WRAP on narrow screens; on 390 px the id drops to its own line
          with `break-all`. The old `flex items-center` row kept everything
          on one line and clipped the id at the right edge. */}
      <div
        className="p-2 mb-3"
        style={{
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          columnGap: 8,
          rowGap: 4,
        }}
      >
        <span style={{ color: C.limeFg, flexShrink: 0 }}>THIS EVENT</span>
        <span style={{ flexShrink: 0 }}>
          <RelativeTime iso={record.evaluated_at} />
        </span>
        <span
          data-testid="event-meta-record-id"
          style={{ wordBreak: 'break-all', flex: '1 1 100%', minWidth: 0 }}
          title={record.record_id} /* title-ok: full id of the wrapped ULID */
        >
          {record.record_id}
        </span>
      </div>

      {/* Expandable event trace */}
      <ActivityEventRow
        C={C}
        record={record}
        buyerReveal={buyerReveal}
        isCurrent={true}
        {...(payload !== undefined ? { payload } : {})}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Raw data tab
// ---------------------------------------------------------------------------

function RawDataTab({
  C,
  record,
  onExportJson,
  onExportHtml,
  exportingJson,
  exportingHtml,
  exportError,
  bundleSplit,
  siteDomain,
  groupVisits,
}: {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
  /** Set when the panel is a grouped row: how many visits it holds. */
  groupVisits?: number | undefined;
  onExportJson: () => void;
  onExportHtml: () => void;
  exportingJson: boolean;
  exportingHtml: boolean;
  exportError: string | null;
  bundleSplit: BundleSplit | null;
  siteDomain?: string | undefined;
}): JSX.Element {
  const api = useApi();
  const is_discovery = record.decision === 'observed';
  const rawId = resolveRawIdentifier(record);
  const id = identityState(record);
  const signer = typeof record.signed_agent === 'string' && record.signed_agent.trim().length > 0
    ? record.signed_agent.trim()
    : null;
  const signerLine = signer === null ? null : [
    signer,
    record.signature_verified === true
      ? 'signature valid'
      : record.signature_verified === false
        ? 'signature failed'
        : 'signature not checked',
    // `signed_agent` is visitor-supplied: only call it an enrolled operator
    // when the server verified the signature (security review Step A m4).
    isSillSignedAgent(signer)
      ? (record.signature_verified === true ? 'Sill-enrolled operator' : 'claims a Sill key directory')
      : knownPublicSignerFor(signer) !== null
        ? "host is on Sill's vetted signer list"
        : "not on Sill's vetted signer list",
  ].join(' · ');
  const seenBy = seenByLabel(record);
  const identityCheck = identityCheckSentence(record, id);
  // De-duplicate against the rows below: the install surface has its own
  // row, and so does the signer.
  // De-duplicate against the rows below (Seen by, Signer, Identity check)
  // and drop the default "Not signed" line (Step C: no default-only rows).
  const evidenceLines = id.evidence.filter((line) =>
    !line.startsWith('Seen by ')
    && !(signerLine !== null && line.startsWith('Signed by '))
    && line !== 'Not signed'
    && !(identityCheck !== null && /^Address (is on|is not on|belongs to)\b/.test(line)));
  const recordSigned = typeof record.envelope_signature === 'string' && record.envelope_signature.length > 0;
  // Visits panel Step C: plain words, and no row that only holds a default
  // ("not recorded", "none"). The signature internals stay under "Signed
  // record" below.
  const rawRows: Array<{ label: string; value: string; signed?: boolean }> = [
    ...(identityCheck !== null ? [{ label: 'IDENTITY CHECK', value: identityCheck }] : []),
    ...(signerLine !== null ? [{ label: 'SIGNER', value: signerLine }] : []),
    ...(seenBy !== null ? [{ label: 'SEEN BY', value: seenBy }] : []),
    {
      label: 'TAMPER-PROOF',
      value: recordSigned ? 'Sill signed this log entry' : 'Not signed',
      signed: recordSigned,
    },
  ];

  return (
    <div>
      {/* Founder 2026-10-19: on a grouped row this tab is ONE visit (the
          newest); every visit has its own signed record. */}
      {groupVisits !== undefined && groupVisits > 1 ? (
        <div
          data-testid="raw-latest-visit-note"
          className="mb-4 p-3"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.body, lineHeight: 1.5, background: C.surface2, border: `1px solid ${C.borderSoft}`, borderRadius: 2 }}
        >
          This is the newest of {groupVisits.toLocaleString('en-US')} visits. Each visit has its own signed record; open one from the Activity tab to see it.
        </div>
      ) : null}
      {/* Identity — every fact behind the list/header marks. Visits panel
          Step A (labels-v2 ux.md §3.3): the ONLY place signer, vetting,
          install-surface and record-signature detail appear. */}
      <div className="mb-5" data-testid="raw-identity">
        <Kicker C={C} label="Identity" />
        <div className="space-y-3">
          {evidenceLines.length > 0 ? (
            <div>
              <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5 }}>
                HOW SILL CHECKED
              </div>
              <ul style={{ margin: 0, marginTop: 2, padding: 0, listStyle: 'none' }}>
                {evidenceLines.map((line, i) => (
                  <li
                    key={i}
                    style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {rawRows.map((row) => (
            <div key={row.label} data-raw-row={row.label}>
              <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5 }}>
                {row.label}
              </div>
              <div
                className="flex items-center gap-1"
                style={{ fontFamily: row.label === 'SIGNER' ? monoFont : sansFont, fontSize: row.label === 'SIGNER' ? 11 : 12, color: C.text, marginTop: 2, wordBreak: 'break-word', lineHeight: 1.5 }}
              >
                {row.signed === true ? <Check size={12} color={C.mintFg} aria-label="Signed" style={{ flexShrink: 0 }} /> : null}
                <span>{row.value}</span>
              </div>
            </div>
          ))}
          {rawId !== null ? (
            <div>
              <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5 }}>
                USER AGENT
              </div>
              <div
                className="flex items-start gap-1"
                style={{ fontFamily: monoFont, fontSize: 11, color: C.text, wordBreak: 'break-all', marginTop: 2, lineHeight: 1.5 }}
              >
                <span style={{ flex: 1 }}>{rawId}</span>
                <CopyButton value={rawId} C={C} />
              </div>
            </div>
          ) : null}
          {record.identification_match?.agent_id !== undefined ? (
            <div>
              <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5 }}>AGENT ID</div>
              <div style={{ fontFamily: monoFont, fontSize: 11, color: C.text, marginTop: 2 }}>
                {record.identification_match.agent_id}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Signed record anchors */}
      <div className="mb-5">
        <Kicker C={C} label="Signed record" />
        <div
          className="p-3"
          style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
        >
          <Anchor C={C} label="record_id" value={record.record_id} />
          <Anchor C={C} label="evaluated_at" value={record.evaluated_at} />
          {record.request_hash !== undefined ? (
            <Anchor C={C} label="request_hash" value={record.request_hash} />
          ) : null}
          {record.response_hash !== undefined ? (
            <Anchor C={C} label="response_hash" value={record.response_hash} />
          ) : null}
          <Anchor C={C} label="envelope_signature" value={record.envelope_signature} />
          <Anchor C={C} label="prev_record_hash" value={record.prev_record_hash} />
          <Anchor C={C} label="merkle_root" value={record.merkle_root} />
          {record.connector_call?.arguments_hash !== undefined ? (
            <Anchor C={C} label="arguments_hash" value={record.connector_call.arguments_hash} />
          ) : null}
        </div>
      </div>

      {/* Not-persisted note */}
      {!is_discovery ? (
        <div
          className="p-3 mb-5"
          style={{
            border: `1px dashed ${C.borderSoft}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          The canonical signed mandate body (intent, principal, agent_id,
          ed25519 signature, key_id) is verified at the edge and intentionally
          NOT persisted server-side. The audit record carries only the sanitized
          summary. The chain anchors prove the record was committed, not the
          mandate's contents.
        </div>
      ) : null}

      {/* Quiet exports */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={onExportJson}
          disabled={exportingJson || exportingHtml}
          className="flex items-center justify-center gap-2 px-3 py-2 flex-1"
          style={{
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            fontFamily: sansFont,
            fontWeight: 500,
            fontSize: 12,
            borderRadius: 2,
            cursor: exportingJson || exportingHtml ? 'not-allowed' : 'pointer',
          }}
        >
          <FileJson size={12} /> {exportingJson ? 'Exporting…' : 'Export JSON'}
        </button>
        <button
          type="button"
          onClick={onExportHtml}
          disabled={exportingJson || exportingHtml}
          className="flex items-center justify-center gap-2 px-3 py-2 flex-1"
          style={{
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            fontFamily: sansFont,
            fontWeight: 500,
            fontSize: 12,
            borderRadius: 2,
            cursor: exportingJson || exportingHtml ? 'not-allowed' : 'pointer',
          }}
        >
          <Download size={12} /> {exportingHtml ? 'Exporting…' : 'Audit bundle'}
        </button>
      </div>
      {exportError !== null ? (
        <div
          className="mt-2 p-2 flex items-start gap-2"
          style={{ background: C.coralDim, border: `1px solid ${C.coralFg}`, borderRadius: 2 }}
        >
          <AlertOctagon size={12} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.4 }}>
            {exportError}
          </span>
        </div>
      ) : null}
      <BundleDayNote C={C} record={record} />
      <BundlePartsNote C={C} api={api} record={record} siteDomain={siteDomain} split={bundleSplit} />
      <NarrowedExportPanel api={api} record={record} siteDomain={siteDomain} />
    </div>
  );
}

/**
 * "last seen" for header line 2: the newest visit we know of for this
 * agent — this record's last visit, or a newer one in the loaded history.
 */
export function latestSeen(record: AuditRecord, history?: readonly AuditRecord[]): string {
  const at = (r: AuditRecord): string => (r as MandateRecord).last_visit_at ?? r.evaluated_at;
  let best = at(record);
  for (const h of history ?? []) {
    const t = at(h);
    if (Date.parse(t) > Date.parse(best)) best = t;
  }
  return best;
}

// ---------------------------------------------------------------------------
// MandateDetailV3 — entry point
// ---------------------------------------------------------------------------

/** "since Sep 30" in the merchant's display time zone (Step B header). */
function sinceDayLabel(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return `since ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone }).format(d)}`;
  } catch {
    return `since ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d)}`;
  }
}

/**
 * Suspicious v2 (founder 2026-10-18) — build the unified
 * DetailActionRow for a MandateDetailV3 record. Keeps the per-variant
 * (verified crawler, impostor, Mismatch, Sill tool, agent-with-key)
 * visibility logic in one spot the panel calls once.
 */
function buildMandateActionRow(args: {
  readonly record: AuditRecord;
  readonly isMismatch: boolean;
  readonly sillTool: boolean;
  readonly impostorLineShown: boolean;
  readonly canManage: boolean;
  readonly currentOverride: 'hold' | 'refuse' | null;
  readonly setOverride: (kind: 'hold' | 'refuse') => void;
  readonly clearOverride: () => void;
  readonly onBlockAllAddresses: () => void;
  /** Founder 2026-10-28 (engin8): greys Block on installs that can't block. */
  readonly blockDisabledReason: string | null;
  readonly onReport: () => void;
  readonly onShareCopyRecord: () => void;
  readonly shareJustCopied: boolean;
}): JSX.Element | null {
  const {
    record, isMismatch, sillTool, impostorLineShown, canManage,
    currentOverride, setOverride, clearOverride,
    onBlockAllAddresses, blockDisabledReason, onReport, onShareCopyRecord, shareJustCopied,
  } = args;
  // Founder bug 2026-10-18: Block availability must be consistent.
  //
  // The rule, written down once:
  //   - Mismatch row (claimed-name sender fails verification): show
  //     Block. The sender-addresses endpoint returns the addresses that
  //     used the claimed name — exactly the Mismatch's addresses.
  //   - Verified crawler + impostor traffic (probing/impostor line
  //     shown): show Block. The addresses that produced the impostor
  //     line are the only ones that can succeed; a Block-all against
  //     the verified crawler's own published ranges is rejected by the
  //     server-side verified-crawler screen (409 verified_crawler), so
  //     the Block only lands on impostor addresses. The real crawler
  //     keeps working.
  //   - Unverified bot: show Block. Its addresses.
  //   - Pure verified crawler, no impostor traffic: hide Block (nothing
  //     to block; the verified line sits where the row would be).
  //   - Sill's own tools: hide Block (own-infra screen is server-side,
  //     but there's no merchant benefit to showing a button that fails).
  const isVerifiedCrawler = record.identity_verification === 'signature'
    || record.identity_verification === 'published_ip'
    || record.identity_verification === 'rdns';
  const showBlock = canManage
    && !sillTool
    && record.agent_key !== undefined
    && !(isVerifiedCrawler && !impostorLineShown);

  // Build the ⋯ menu callbacks. Hold / Refuse requires a resolved
  // agent_id (from identification_match or a rejection draft) because
  // the override KV is keyed on `agent_id`, not `agent_key`. Role
  // enforcement happens server-side on PUT/DELETE
  // /agent-overrides/:agent_id — matching main's prior UI, we do NOT
  // hide Hold / Refuse on reviewer / viewer; the backend 403s on write.
  const override_agent_id = record.identification_match?.agent_id ?? record.rejected_agent_id;
  const canHoldRefuse = !isMismatch
    && !sillTool
    && !impostorLineShown
    && override_agent_id !== undefined;
  // Report to network — same posture as prod: show when there's an
  // agent_key, not gated on role (the POST is role-checked).
  const canReport = !isMismatch
    && !sillTool
    && record.agent_key !== undefined;

  // Everybody can Share, even reviewer/viewer.
  const props = {
    blockMode: showBlock ? ('addresses' as const) : ('none' as const),
    ...(showBlock ? { onBlock: () => onBlockAllAddresses() } : {}),
    blockDisabledReason,
    // Hold is not offered: the edge doesn't gate on it yet, so it would be a
    // button that changes nothing. An existing hold can still be cleared.
    ...(canHoldRefuse ? { onRefuse: () => setOverride('refuse') } : {}),
    ...(canHoldRefuse ? { onClearOverride: () => clearOverride() } : {}),
    currentOverride,
    ...(canReport ? { onReport } : {}),
    onShare: onShareCopyRecord,
    shareJustCopied,
  };
  return <DetailActionRow {...props} />;
}

export function MandateDetailV3({
  record,
  payload,
  buyerReveal,
  containerLayout,
  siteDomain,
  setView,
  onViewBotProfile,
  onSearchAddress,
  onOpenAttackers,
  group,
  groupWindow = '',
  groupActivity,
}: {
  record: AuditRecord;
  payload?: AuditRecordPayload;
  buyerReveal: 'auto' | 'on_click';
  containerLayout: 'sticky' | 'flat';
  siteDomain?: string;
  setView?: NavToGuardrails;
  onViewBotProfile?: (agentKey: string, displayName: string) => void;
  /** Show every visit from this address (a scanner address was clicked). */
  onSearchAddress?: (address: string) => void;
  /** Founder bug 2026-10-20 — open Security › Attackers for a sender.
   *  `threat_ref` is `null` when the match is blocklist-only. */
  onOpenAttackers?: (threat_ref: string | null) => void;
  /**
   * Visits panel Step B — the grouped row this panel was opened from
   * (`record` is its newest record). The header counts, last seen and the
   * Confirmed mark then describe the whole group over the list's range.
   * Ignored for one-record groups (orders, mandates).
   */
  group?: VisitGroup;
  /** Window label for the group count, e.g. "since 2026-10-01"; '' = all. */
  groupWindow?: string;
  /** Activity tab body for the group (its records; click opens one). */
  groupActivity?: ReactNode;
}): JSX.Element {
  const activeGroup = group !== undefined && group.kind !== 'record' ? group : undefined;
  const displayTimeZone = useDisplayTimeZone();
  const C = useC();
  const isShopify = useIsShopifySite(record.site_id);
  const api = useApi();
  const http = useOptionalHttp();
  // Suspicious v2 §4.2 — owner/admin unlocks the per-row Block +
  // Block-all buttons in BotAddressBlockSection.
  const authForBlock = useOptionalAuth();
  const canManage = authForBlock !== null
    && authForBlock.state.kind === 'authenticated'
    && (authForBlock.state.user.role === 'owner' || authForBlock.state.user.role === 'admin');
  // Founder 2026-10-28 (engin8): the install decides whether Block can
  // work; a WordPress-plugin-only site gets a greyed Block with the reason.
  const [blocking, setBlocking] = useState<BlockingState | null>(null);
  useEffect(() => {
    if (http === null || !canManage) return;
    let live = true;
    void getBlocking(http, record.site_id)
      .then((r) => { if (live && r != null && r.blocking != null) setBlocking(r.blocking); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [http, canManage, record.site_id]);

  const [tab, setTab] = useState<V3Tab>('overview');
  const [exportingJson, setExportingJson] = useState(false);
  const [exportingHtml, setExportingHtml] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [bundleSplit, setBundleSplit] = useState<BundleSplit | null>(null);
  const [copied, setCopied] = useState(false);

  // Phase B — agent history: load all records for this agent on this site.
  const [agentHistory, setAgentHistory] = useState<AuditRecord[] | undefined>(undefined);
  const [agentHistoryLoading, setAgentHistoryLoading] = useState(false);

  const agent_id_for_history =
    record.identification_match?.agent_id ?? record.rejected_agent_id;

  useEffect(() => {
    // Clear immediately so Activity tab never shows a previous agent's sessions.
    setAgentHistory(undefined);
    if (agent_id_for_history === undefined) return;
    let cancelled = false;
    setAgentHistoryLoading(true);
    void api.mandates
      .listByAgent(record.site_id, agent_id_for_history)
      .then((pg) => {
        if (cancelled) return;
        // Mismatch: history is keyed on the CLAIMED agent id, so it also
        // holds the real agent's visits. Keep only this sender's
        // server-flagged visits so Top pages / Activity never pin the real
        // Googlebot's traffic on the impersonator (ADR-0229 §9).
        // And the reverse (security review Step B m3): a real agent's panel
        // never counts an impersonator's visits under the same claimed id.
        setAgentHistory(identityState(record).state === 'mismatch'
          ? pg.records.filter((h) => identityState(h).state === 'mismatch')
          : pg.records.filter((h) => identityState(h).state !== 'mismatch'));
      })
      .catch(() => { /* silently fall back to single-event view */ })
      .finally(() => { if (!cancelled) setAgentHistoryLoading(false); });
    return () => { cancelled = true; };
  // Re-fetch when the subject record changes (navigating between agents).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.record_id]);

  // Network stat tiles — cross-merchant aggregate from agent_observed.
  const [networkStats, setNetworkStats] = useState<AgentNetworkStats | null | undefined>(undefined);
  useEffect(() => {
    const key = record.agent_key;
    if (key === undefined || http === null) { setNetworkStats(null); return; }
    void http.request<AgentNetworkStats>({
      method: 'GET',
      path: `/v1/bots/${encodeURIComponent(key)}/network-stats`,
    })
      .then((stats) => { setNetworkStats(stats ?? null); })
      .catch(() => { setNetworkStats(null); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.record_id]);

  // Header line 2 count — this agent's visits over the last 7 days, from
  // the server's daily rollups (`GET /v1/sites/:site/agent-rollups?range=7d
  // &agent_key=`; page loads, the same "traffic" figure the Agents page
  // shows, summed over every build of the identity). `null` until loaded or
  // when unavailable, and then the count is simply omitted. Never fetched
  // for Mismatch or people: their line 2 carries no count.
  const [weekVisits, setWeekVisits] = useState<number | null>(null);
  useEffect(() => {
    setWeekVisits(null);
    const key = record.agent_key;
    const st = identityState(record).state;
    // Step B: a grouped row brings its own range count.
    if (activeGroup !== undefined) return;
    if (key === undefined || st === 'mismatch' || st === 'person') return;
    let cancelled = false;
    void api.agentRollups
      .get(record.site_id, '7d', key)
      .then((res) => {
        if (cancelled) return;
        const total = res.agents.reduce((n, a) => n + (Number.isFinite(a.page_loads) ? a.page_loads : 0), 0);
        setWeekVisits(total > 0 ? total : null);
      })
      .catch(() => { /* best-effort — omit the count */ });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.record_id]);

  // Hold / Refuse override state
  const override_agent_id = agent_id_for_history;
  const [currentOverride, setCurrentOverride] = useState<'hold' | 'refuse' | null>(null);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  // Load existing override on mount / agent change
  useEffect(() => {
    if (override_agent_id === undefined || http === null) return;
    void http.request<{ overrides: Array<{ agent_id: string; override: string }> }>({
      method: 'GET',
      path: `/v1/sites/${record.site_id}/agent-overrides`,
    }).then((res) => {
      const match = res?.overrides.find((o) => o.agent_id === override_agent_id);
      setCurrentOverride(
        match?.override === 'hold' ? 'hold'
        : match?.override === 'refuse' ? 'refuse'
        : null,
      );
    }).catch(() => { /* silently — optional feature */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.record_id]);

  const setOverride = (kind: 'hold' | 'refuse'): void => {
    if (override_agent_id === undefined || http === null) return;
    setOverrideLoading(true);
    setOverrideError(null);
    void http.request<{ agent_id: string; override: string }>({
      method: 'PUT',
      path: `/v1/sites/${record.site_id}/agent-overrides/${encodeURIComponent(override_agent_id)}`,
      body: { override: kind },
    }).then(() => {
      setCurrentOverride(kind);
    }).catch((err: unknown) => {
      setOverrideError(err instanceof Error ? err.message : 'Failed to set override.');
    }).finally(() => { setOverrideLoading(false); });
  };

  const clearOverride = (): void => {
    if (override_agent_id === undefined || http === null) return;
    setOverrideLoading(true);
    setOverrideError(null);
    void http.request<unknown>({
      method: 'DELETE',
      path: `/v1/sites/${record.site_id}/agent-overrides/${encodeURIComponent(override_agent_id)}`,
    }).then(() => {
      setCurrentOverride(null);
    }).catch((err: unknown) => {
      setOverrideError(err instanceof Error ? err.message : 'Failed to clear override.');
    }).finally(() => { setOverrideLoading(false); });
  };

  // Suspicious v2 (founder 2026-10-18) — bus for the DetailActionRow's
  // "Block ▾" → BotAddressBlockSection's Block all N flow. The row
  // increments `blockAllTick`; the section watches the counter and
  // runs its Block-all under the active duration.
  const [blockAllTick, setBlockAllTick] = useState(0);
  const onBlockAllHandled = useCallback(() => setBlockAllTick(0), []);
  // Report-to-network from the ⋯ menu (replaces the big
  // ReportToNetworkButton that used to live at the bottom of Overview).
  // Agent_key is required; the ⋯ item is hidden otherwise.
  const [reportedViaMenu, setReportedViaMenu] = useState(false);
  const onReport = useCallback(() => {
    if (http === null || record.agent_key === undefined || reportedViaMenu) return;
    setReportedViaMenu(true);
    void http.request({
      method: 'POST',
      path: `/v1/bots/${encodeURIComponent(record.agent_key)}/report`,
    }).catch(() => { /* best-effort */ });
  }, [http, record.agent_key, reportedViaMenu]);

  const onExportJson = (): void => {
    setExportingJson(true);
    setExportError(null);
    void exportAuditRecordJSON(api, record, siteDomain)
      .then(setBundleSplit)
      .catch((err: unknown) => {
        setExportError(exportErrorMessage('Export', err));
      })
      .finally(() => { setExportingJson(false); });
  };

  const onExportHtml = (): void => {
    setExportingHtml(true);
    setExportError(null);
    void exportAuditBundleHTML(api, record, siteDomain)
      .then(setBundleSplit)
      .catch((err: unknown) => {
        setExportError(exportErrorMessage('Bundle export', err));
      })
      .finally(() => { setExportingHtml(false); });
  };

  const agentName = resolveAgentHeadline(record);
  const outcome = outcomePresentation(record);
  // Visits panel Step A (labels-v2): identity comes ONLY from the server
  // verdict via `identityState()`. At most two lines above the tabs.
  const idResult = identityState(record);
  const isMismatch = idResult.state === 'mismatch';
  // Step B: with no date filter the range is every retained record, so the
  // count still needs its window: "since Sep 30" (the row's first visit).
  const groupWindowText = activeGroup === undefined || groupWindow.length > 0
    ? groupWindow
    : sinceDayLabel(activeGroup.first_seen, displayTimeZone);
  const line2Counts = activeGroup !== undefined
    ? { visits: activeGroup.visits, visitsWindow: groupWindowText }
    : { visits: weekVisits, visitsWindow: 'in 7 days' };
  // The `people` row stands for every person in range; a `p.<id>` row
  // (one recognised person) reads like one human record.
  const isAllPeople = activeGroup !== undefined && activeGroup.kind === 'people' && !isPersonGroupKey(activeGroup.group_key);
  const isPersonRow = activeGroup !== undefined && isPersonGroupKey(activeGroup.group_key);
  // A human visit's line 2 leads with "Chrome on Windows" (no version).
  const humanDevice = (activeGroup === undefined || isPersonRow) && isHumanVisit(record) ? humanRecordDevice(record) : null;
  const line2Parts = activeGroup?.kind === 'people' || isPersonRow
    ? [humanDevice, headerCountText(line2Counts)].filter((p): p is string => p !== null)
    : activeGroup === undefined && isHumanVisit(record)
      ? [humanDevice].filter((p): p is string => p !== null)
      : headerLine2Parts(record, idResult, line2Counts);
  const line2Count = headerCountText(line2Counts);
  // "OpenAI · GPTBot" → "GPTBot" when line 2 already names the operator,
  // so the header matches the list row.
  const operatorOnLine2 = line2Parts !== null && visitOperator(record) !== null;
  // Step C: a bot-token row names the bot, not one version's headline.
  const tokenRowName = activeGroup !== undefined && activeGroup.group_key.startsWith('a.tok.')
    ? visitGroupName(activeGroup)
    : null;
  const isScannerRow = activeGroup !== undefined && isScannerGroupKey(activeGroup.group_key);
  // ops2026 UCP checkout (2026-10-21) — an agent-platform checkout never
  // reads as "Unidentified visitor". The server-signed `inbound_protocol
  // === 'ucp'` + the resolved platform_host let us lead the header with
  // the platform the merchant actually sees in their rules (ops.sill.so,
  // or "Sill test platform" for staff-run checks). Line 2 drops the
  // visitor-style count and reads "Agent checkout · UCP".
  const ucpOrder = isUcpOrder(record);
  const ucpHeader = ucpOrder ? ucpPlatformHeader(record) : null;
  const displayName = ucpHeader !== null
    ? ucpHeader.name
    : (isPersonRow || isScannerRow) && activeGroup !== undefined
    ? visitGroupName(activeGroup)
    : activeGroup?.kind === 'people'
    ? 'People'
    : tokenRowName !== null
    ? tokenRowName
    : isMismatch
    ? quotedName(idResult.name)
    : operatorOnLine2 && agentName.includes(' · ')
      ? (agentName.split(' · ').pop() ?? agentName)
      : agentName;
  const reasonLine = mismatchReason(record, idResult);
  // Probing (ADR-0244: rare and quiet). The header line only for
  // meaningful probing (`probeAlert`: no registry name, 5+ requests, 25%+
  // of visits; or a registry-named row with 3+ universal probe requests,
  // which reads as an impostor using the name). A registry-named row with
  // probe hits under that bar gets a quiet note in Key facts instead.
  const probe = probeFacts(activeGroup ?? null, record);
  const probeCtx = probeContext(activeGroup, record);
  const headerDescription = ucpHeader !== null
    ? ucpHeader.subline
    : visitHeaderDescription(record, idResult, activeGroup?.kind === 'people' || isPersonRow);
  // Sill's own tools: no borrowed-name note, no Report / Hold / Refuse.
  // Their rows alert only on universal probes from unverified visits
  // (server `probe_alert`, security review m1).
  const sillTool = isSillToolVisit(record);
  // Founder 2026-10-19 — the visit's IP for the Location row + Block fallback.
  const recordAddress = useRecordAddress(record.site_id, record.record_id, !sillTool);
  const headerProbe = probe !== null && activeGroup !== undefined && activeGroup.kind === 'agent' && probeAlert(activeGroup)
    ? probe
    : null;
  // ADR-0244: an alerting registry-named row speaks about the impostor
  // ("Someone using the {Name} name…"), with no WAF hint.
  const impostorLine = headerProbe !== null && activeGroup !== undefined && !sillTool
    ? impostorProbeLine(activeGroup, displayName, activeGroup.probe_examples ?? [])
    : null;
  // The quiet borrowed-name note stays for named rows that do NOT alert.
  const probeNote = !sillTool && impostorLine === null && probe !== null && activeGroup !== undefined && activeGroup.kind === 'agent' && probeCtx.registryName
    ? probeBorrowedNameNote(probe.hits, displayName)
    : undefined;
  const callout = mismatchCallout(record, idResult);
  const lastSeen = activeGroup !== undefined ? activeGroup.last_seen : latestSeen(record, agentHistory);
  // Step B: an agent row's mark comes from its newest Confirmed record (if
  // any) and says for how many visits; never from the newest record alone.
  const groupExample = activeGroup?.kind === 'agent' ? activeGroup.confirmed_example : undefined;
  // Founder 2026-10-18: when the AgentIdentitySplit renders below the
  // header line 2, the header mark drops to a bare "Confirmed" (no
  // "in N of M records" / "N of M visits came from…" tail) because
  // the split carries the detailed breakdown. The old
  // `groupConfirmedLabel` / `groupConfirmedNote` output is still used
  // on surfaces that don't render the split (Mismatch rows, record-
  // level fallback) so no copy was deleted.
  const splitShown = activeGroup?.kind === 'agent' && record.agent_key !== undefined && !isMismatch && !sillTool;
  const headerMark = activeGroup?.kind === 'agent'
    ? (groupExample !== undefined
      ? (() => {
          const label = splitShown ? 'Confirmed' : (groupConfirmedLabel(activeGroup) ?? 'Confirmed');
          // Partly confirmed: the visible label already says "in X of Y
          // records"; the tooltip keeps just the proof line.
          const partial = label !== 'Confirmed';
          return <VisitIdentityMark record={groupExample} result={identityState(groupExample)} withLabel label={label} note={partial || splitShown ? null : groupConfirmedNote(activeGroup)} />;
        })()
      : null)
    : <VisitIdentityMark record={record} result={idResult} withLabel />;

  const cardClassName =
    containerLayout === 'flat' ? 'p-5' : 'p-5 sticky top-24 overflow-y-auto';
  const cardStyle =
    containerLayout === 'flat' ? undefined : { maxHeight: 'calc(100vh - 7rem)' };

  return (
    <Card
      className={cardClassName}
      {...(cardStyle !== undefined ? { style: cardStyle } : {})}
    >
      {/* ── HEADER (≤ 2 lines above the tabs) ── */}
      <div className="mb-4" data-testid="visit-header">
        {/* Line 1 — logo · name · mark … decision pill · Block ▾ · ⋯
            Founder bug 2026-10-18: narrow panels used to clip the
            Block button into the title, wrapping "ChatGPT user" /
            "browse" onto two lines. flexWrap pushes the actions onto
            their own row when the title + actions would otherwise
            overflow — the title now has the whole row to itself.

            ops2026 UCP polish (2026-10-21): on a UCP record the title
            row carries ONLY the logo + name on the left and the ⋯ on
            the right (no pills, no trust chip, no outcome tag). The
            decision pill, SILL TEST pill and trust chip then live on
            one dedicated left-aligned row below — see "Line 1b" below.
            This keeps the title row from overflowing on 390 px and
            moves the "ESCALATED → APPROVED / SILL TEST" cluster from
            a floating right-anchored block back into reading order. */}
        <div
          className="flex items-start gap-3"
          style={{ justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', rowGap: 8 }}
        >
          <div
            className="flex items-center gap-2 min-w-0 flex-wrap"
            data-testid="visit-header-line1"
            style={{ flex: '1 1 auto', minWidth: 0 }}
          >
            <VisitLogoGlyph record={record} result={idResult} size={16} flag={!isAllPeople} />
            <h2
              style={{
                fontFamily: serifFont,
                fontSize: 20,
                color: C.text,
                letterSpacing: -0.4,
                // Instrument Serif's space is narrow; with the tight
                // tracking "Sill · Site scanner" read as "Sill·Sitescanner".
                wordSpacing: '0.18em',
                lineHeight: 1.2,
                margin: 0,
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              {displayName}
            </h2>
            {/* Confirmed/Mismatch mark and the merchant trust chip sit
                beside the name. On UCP the one "Signed and verified"
                mark takes that place (founder 2026-10-28). */}
            {ucpOrder ? <UcpPlatformTrustMark C={C} surface="header" auth={ucpPlatformAuth(record)} /> : headerMark}
            {!ucpOrder && (activeGroup?.kind === 'people' || isPersonRow) ? null
              : ucpOrder ? null : (
              <VisitTrustChip
                record={activeGroup?.kind === 'agent' ? (activeGroup.confirmed_example ?? record) : record}
                name={displayName}
                surface="header"
                mismatch={isMismatch}
                probing={headerProbe !== null}
              />
            )}
            {/* Founder bug 2026-10-20 — Activity ↔ Security consistency.
                A sender that Security has already flagged shows a coral
                chip in the detail header too; clicking it opens Security
                › Attackers on the matching sender. */}
            {(() => {
              const sec = activeGroup?.kind === 'agent'
                ? (activeGroup.confirmed_example?.security_flag ?? record.security_flag)
                : record.security_flag;
              if (sec === undefined) return null;
              const chipSetView: ChipSetView | undefined = onOpenAttackers !== undefined
                ? (_v, opts) => onOpenAttackers(opts?.threat ?? null)
                : undefined;
              return (
                <SecurityFlagChip
                  flag={sec}
                  surface="header"
                  {...(chipSetView !== undefined ? { setView: chipSetView as unknown as import('../../app/view-router.js').SetView } : {})}
                />
              );
            })()}
          </div>
          <div
            className="flex items-center gap-2 flex-shrink-0"
            style={{ marginLeft: 'auto' }}
          >
            {/* On UCP, the decision pill and SILL TEST pill move to
                Line 1b below. Only the ⋯ action row is pinned right on
                the title row. */}
            {!ucpOrder && outcome.label !== 'OBSERVED' ? (
              <Tooltip
                content="A policy-evaluated request — approved, rejected, or escalated based on your rules."
                side="bottom"
              >
                <Pill tone={outcome.tone}>{outcome.label}</Pill>
              </Tooltip>
            ) : null}
            {/* ops2026 UCP checkout test (2026-10-21). Neutral pill when
                the record belongs to a staff-run test (unsigned
                `staff_test` field, see services/api/src/ucp/staff-test.ts). */}
            {!ucpOrder && record.staff_test === true ? (
              <Tooltip
                content="Run from Sill's ops console to check this site's checkout. Not counted in your numbers."
                side="bottom"
              >
                <Pill tone="neutral">SILL TEST</Pill>
              </Tooltip>
            ) : null}
            {/* Suspicious v2 (founder 2026-10-18) — unified action
                row. See `buildMandateActionRow` below for the
                variant-aware props (Block ▾ for bots/agents/scanners;
                omitted for verified crawlers; ⋯ carries Hold / Refuse
                / Report / Share, each with a one-liner description). */}
            {buildMandateActionRow({
              record,
              isMismatch,
              sillTool,
              impostorLineShown: impostorLine !== null,
              canManage,
              currentOverride,
              setOverride,
              clearOverride,
              // The address list (and its confirm) lives on Overview:
              // switch there so a click from another tab is not lost.
              onBlockAllAddresses: () => { setTab('overview'); setBlockAllTick((n) => n + 1); },
              blockDisabledReason: blockUnavailableReason(blocking, { shopify: isShopify }),
              onReport,
              onShareCopyRecord: () => {
                try {
                  const url = new URL(window.location.href);
                  // A grouped row (e.g. "Unknown scanner") shares the row,
                  // not just its newest visit (founder 2026-10-19).
                  if (activeGroup !== undefined) {
                    url.searchParams.delete('record');
                    url.searchParams.set('group', activeGroup.group_key);
                  } else {
                    url.searchParams.delete('group');
                    url.searchParams.set('record', record.record_id);
                  }
                  void navigator.clipboard.writeText(url.toString());
                  setCopied(true);
                  const t = setTimeout(() => { setCopied(false); }, 2000);
                  return () => { clearTimeout(t); };
                } catch { /* clipboard blocked */ }
              },
              shareJustCopied: copied,
            })}
          </div>
        </div>

        {/* Line 1b — UCP only: decision pill · SILL TEST · trust chip.
            Left-aligned so the merchant reads status in sentence order
            (decision first, then "Sill test" if applicable, then the
            platform-trust statement). 8 px gap matches the rest of the
            header; wraps cleanly at 390 px. */}
        {ucpOrder ? (
          <div
            data-testid="visit-header-line1b"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
            }}
          >
            {outcome.label !== 'OBSERVED' ? (
              <Tooltip
                content="A policy-evaluated request — approved, rejected, or escalated based on your rules."
                side="bottom"
              >
                <Pill tone={outcome.tone}>{outcome.label}</Pill>
              </Tooltip>
            ) : null}
            {record.staff_test === true ? (
              <Tooltip
                content="Run from Sill's ops console to check this site's checkout. Not counted in your numbers."
                side="bottom"
              >
                <Pill tone="neutral">SILL TEST</Pill>
              </Tooltip>
            ) : null}
          </div>
        ) : null}

        {/* Description under the name (founder 2026-10-14): the AI
            description of the bot (ai_summary.who), or a Sill tool's
            merchant-facing purpose. Muted, two lines at most; the full
            text is in the tooltip. */}
        {headerDescription !== null ? (
          <div
            data-testid="visit-header-description"
            title={headerDescription} /* title-ok: full text of the 2-line-clamped description */
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.5,
              marginBottom: 4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
            }}
          >
            {headerDescription}
          </div>
        ) : null}

        {/* Line 2 — "{Purpose} · {Operator} · {N} visits in 7 days · last
            seen {t}" (muted), or the Mismatch reason (coral). People have
            no line 2. Counts come from the server rollup; unknown parts
            are omitted, never invented. UCP orders use their own
            "Agent checkout · UCP" subline (via headerDescription) and
            hide the visitor-style count — line 2 would read "Bot ·
            68 visits in 7 days", which is nonsense for an agent order. */}
        {ucpOrder ? null : isMismatch && reasonLine !== null ? (
          <div
            data-testid="visit-header-line2"
            style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5, marginBottom: 8 }}
          >
            {reasonLine}
          </div>
        ) : line2Parts !== null ? (
          // Mono readout, one size under the sans description above, so
          // the facts line never reads as a second sentence (founder
          // 2026-10-27). Same pattern as the session summary line.
          <div
            data-testid="visit-header-line2"
            style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}
          >
            {line2Parts.map((part, i) => (
              <span key={i}>
                {i > 0 ? ' · ' : null}
                {part === line2Count ? (
                  <Tooltip content={HEADER_COUNT_TOOLTIP} displayMode="inline" data-testid="visit-header-count">
                    <span>{part}</span>
                  </Tooltip>
                ) : part}
              </span>
            ))}
            {line2Parts.length > 0 ? ' · last seen ' : 'Last seen '}
            <RelativeTime iso={lastSeen} />
          </div>
        ) : null}

        {/* ADR-0247 follow-up (founder 2026-10-18) — identity split on
            an agent panel: "Verified · N visits from K addresses" +
            "Unverified · N visits from K addresses — not proven to
            be <Operator>". Replaces the older groupConfirmedNote line
            when attested data is available; falls back to nothing on
            Mismatch / people / Sill tool / record-level panels. */}
        {record.agent_key !== undefined
          && !isMismatch && !sillTool
          && !isAllPeople
          && !isPersonRow
          && !ucpOrder
          && activeGroup?.kind !== 'people'
          ? (
          <AgentIdentitySplit
            siteId={record.site_id}
            agentKey={record.agent_key}
            operator={record.identity_evidence?.operator_display ?? null}
            {...(headerProbe !== null ? { probing: true } : {})}
          />
        ) : null}

        {/* Probing: one quiet line, neutral text, small warning icon. */}
        {headerProbe !== null ? (
          <div data-testid="visit-header-probe" style={{ marginBottom: 8 }}>
            <div
              className="flex items-start gap-1.5"
              style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}
            >
              <TriangleAlert size={12} color={C.amberFg} aria-hidden="true" style={{ flexShrink: 0, marginTop: 3 }} />
              {impostorLine !== null ? (
                <span data-testid="visit-header-probe-impostor" style={{ overflowWrap: 'anywhere' }}>
                  {impostorLine.lead}
                  {impostorLine.example !== null ? <> like <ProbeExamples examples={[impostorLine.example]} /></> : null}
                  {impostorLine.tail}
                </span>
              ) : (
                <span style={{ overflowWrap: 'anywhere' }}>
                  {probeRequestsLead(headerProbe.hits)}
                  {headerProbe.examples.length > 0 ? <>, like <ProbeExamples examples={headerProbe.examples} /></> : null}
                </span>
              )}
            </div>
            {impostorLine !== null ? (
              impostorLine.blockHint !== null ? (
                <div
                  data-testid="visit-header-probe-impostor-hint"
                  style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5, marginTop: 2, paddingLeft: 18 }}
                >
                  {impostorLine.blockHint}
                </div>
              ) : null
            ) : probeShowsBlockHint(probeCtx) ? (
              <div
                data-testid="visit-header-probe-hint"
                style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5, marginTop: 2, paddingLeft: 18 }}
              >
                {PROBE_BLOCK_HINT}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Tab bar */}
        <TabBar C={C} value={tab} onChange={setTab} />
      </div>

      {/* ── Top of Overview: Mismatch callout, then Hold / Refuse ── */}
      {tab === 'overview' && callout !== null ? (
        <div
          className="p-3 mb-4"
          data-testid="mismatch-callout"
          style={{
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 12,
            color: C.text,
            lineHeight: 1.5,
          }}
        >
          {callout}
        </div>
      ) : null}

      {/* ADR-0246 — the Unknown scanner row lists the addresses it came
          from (server-side role gate: owner / admin / reviewer). */}
      {tab === 'overview' && isScannerRow ? <ScannerIpsSection siteId={record.site_id} {...(onSearchAddress !== undefined ? { onSearchAddress } : {})} /> : null}

      {/* Suspicious v2 §4.2 — verified crawler one-liner. A row with a
          server-stamped identity_verification in {signature,
          published_ip, rdns} is an actual verified crawler and gets a
          "Sill never blocks verified crawlers." line here INSTEAD of a
          Block section. */}
      {tab === 'overview'
        && !sillTool
        && !isMismatch
        && impostorLine === null
        && (record.identity_verification === 'signature'
          || record.identity_verification === 'published_ip'
          || record.identity_verification === 'rdns')
      ? (
        <div
          className="mb-4"
          data-testid="verified-crawler-line"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.body,
            padding: '8px 10px',
            border: `1px solid ${C.mintFg}`,
            borderRadius: 2,
          }}
        >
          Verified {displayName}. Sill never blocks verified crawlers. To keep {displayName} out of pages, add a robots.txt rule.
        </div>
      ) : null}

      {/* Suspicious v2 §4.2 — "Block the addresses this bot used"
          (unverified / unknown) or "Block the addresses using this
          name" (impostor). Sill-tools skip by convention. Mismatches
          already route to the firewall copy via the callout above.
          Founder 2026-10-19: verified crawlers list their addresses too
          ("search engines, SEO tools, AI agents and all bots do not
          surface the IP anywhere"); verified addresses show a VERIFIED
          mark and no Block button. */}
      {tab === 'overview'
        && !sillTool
        && !isMismatch
        && record.agent_key !== undefined
      ? (
        <BotAddressBlockSection
          siteId={record.site_id}
          agentKey={record.agent_key}
          canMutate={canManage}
          fallbackAddress={recordAddress}
          variant={impostorLine !== null ? 'impostor' : 'unverified'}
          externalBlockAllTick={blockAllTick}
          onExternalBlockAllHandled={onBlockAllHandled}
        />
      ) : null}


      {/* Hold / Refuse actions — only when agent_id is known. Visits panel
          Step A: moved below the tabs (top of Overview) so the header stays
          at two lines. The amber "identity is unverified" box is removed.
          Suspicious v2 §4.2: hidden on impostor rows (the BLOCK section
          above covers the right action for a sender using someone else's
          name). */}
      {/* Hidden on Mismatch: the override is keyed on the matched agent_id,
          i.e. the REAL agent whose name this sender used. The callout above
          points the merchant at their firewall instead. */}
      {/* Never on Sill's own tools (founder 2026-10-14). */}
      {/* Suspicious v2 (founder 2026-10-18) — the big Hold / Refuse
          button pair is gone; those actions live in the top-right
          DetailActionRow's ⋯ menu (with Report, Share, Trust, etc.). The
          error inline below keeps the state visible when a flip fails. */}
      {tab === 'overview' && overrideError !== null ? (
        <div data-testid="hold-refuse-error" style={{ marginBottom: 10, fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>
          {overrideError}
        </div>
      ) : null}


      {/* ── TAB BODY ── */}
      {tab === 'overview' ? (
        <OverviewTab
          C={C}
          record={record}
          identityResult={idResult}
          {...(setView !== undefined ? { setView } : {})}
          {...(onViewBotProfile !== undefined && activeGroup?.kind !== 'people' ? { onViewBotProfile } : {})}
          {...(activeGroup !== undefined ? { groupVisits: activeGroup.visits, groupLastSeen: activeGroup.last_seen } : {})}
          {...(probeNote !== undefined ? { probeNote } : {})}
          headerDescription={headerDescription}
          recordAddress={recordAddress}
          {...(activeGroup !== undefined
            // Step B: Total events / First seen describe the grouped row's
            // records over the list's range, like the header.
            ? {
                agentEventCount: activeGroup.records,
                // ACTIONS comes from the agent's history (not range-filtered),
                // so it is shown only when the range is unbounded, i.e. covers
                // the same window as the VISITS tile beside it.
                ...(groupWindow.length === 0
                  ? {
                      agentActionCount: (agentHistory ?? []).filter(
                        (h) => h.skill_id !== undefined || h.connector_call?.skill_id !== undefined,
                      ).length,
                    }
                  : {}),
                agentFirstSeen: activeGroup.first_seen,
                eventsAreRecords: true,
              }
            : agentHistory !== undefined && agentHistory.length > 0
            ? {
                agentEventCount: agentHistory.length,
                agentActionCount: agentHistory.filter(
                  (h) => h.skill_id !== undefined || h.connector_call?.skill_id !== undefined,
                ).length,
                ...(agentHistory[agentHistory.length - 1] !== undefined
                  ? { agentFirstSeen: agentHistory[agentHistory.length - 1]!.evaluated_at as string }
                  : {}),
              }
            : {})}
          {...(networkStats !== undefined ? { networkStats } : {})}
          {...(agentHistory !== undefined && agentHistory.length > 0 ? { agentHistory } : {})}
        />
      ) : tab === 'activity' && groupActivity !== undefined && activeGroup !== undefined ? (
        groupActivity
      ) : tab === 'activity' ? (
        <ActivityTab
          C={C}
          record={record}
          buyerReveal={buyerReveal}
          agentHistoryLoading={agentHistoryLoading}
          {...(agentHistory !== undefined ? { agentHistory } : {})}
          {...(payload !== undefined ? { payload } : {})}
        />
      ) : (
        <RawDataTab
          C={C}
          record={record}
          onExportJson={onExportJson}
          onExportHtml={onExportHtml}
          exportingJson={exportingJson}
          exportingHtml={exportingHtml}
          exportError={exportError}
          bundleSplit={bundleSplit}
          siteDomain={siteDomain}
          groupVisits={activeGroup?.visits}
        />
      )}
    </Card>
  );
}
